// @refresh reset — dev-only: force remount so the useMemo([]) board nodes hot-reload on edit (no prod effect)
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { type Node, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";
import { DocumentFrame } from "./DocumentFrame";
import { MaxConnectBedrock, MaxConnectTeam } from "./command/max-connect";
import { BlockRenderer, type Block } from "../blueprint/BlockRenderer";
import { pitchDeckConfig } from "../blueprint/PitchDeckBlocks";
import { useApi } from "../../../lib/api";
// The strategy / architecture board (engagement → discovery → root cause →
// solution → where it runs → priced options), rendered as one node BELOW the
// wireframes so MAX Security is a single consolidated board.
import { BoardNode as StrategyBoardNode, GROUPS as STRATEGY_GROUPS } from "./MaxSecurityBoardPage";

// ═══════════════════════════════════════════════════════════════════════════
// MAX SECURITY — THE SYSTEM (product-design wireframe board).
// The MAX build, screen by screen, in the Tailormind family: a fork of the
// Boiling Offers architecture (Pydantic-AI + LiteLLM + DBOS + pgvector +
// guardrails + assistant-ui), pointed at MAX and running on their own isolated
// box. The spine = the Client Snapshot / Single Brain (one connected,
// persistent, trustworthy record per entity) + per-role collaborative agents
// that DRAFT · VALIDATE · FLAG — a human approves before anything writes back.
// Every screen is a real styled mock (crisp at any zoom); each is paired with
// an explanation card listing the discovery problem-IDs it covers.
// Owned by Emma. Design language copied from the Boiling Offers board.
// ═══════════════════════════════════════════════════════════════════════════

const T = { bg: "#E5E5E5", dots: "#CFCFCF", ink: "#1C1917", ink2: "#57534E", line: "#E4E0DA", card: "#FFFFFF" };
const FONT = "Inter, system-ui, -apple-system, sans-serif";

function rich(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
    seg.startsWith("**") && seg.endsWith("**") ? (
      <b key={i} style={{ color: T.ink, fontWeight: 700 }}>{seg.slice(2, -2)}</b>
    ) : (
      <span key={i}>{seg}</span>
    ),
  );
}

// ── app-mockup palette ───────────────────────────────────────────────────────
const A = {
  appBg: "#F3F4F6", panel: "#FFFFFF", soft: "#F8F9FB", border: "#E6E8EC",
  ink: "#15171C", ink2: "#5B6270", ink3: "#8A909C", navy: "#0E1524",
  petrol: "#2B5560", green: "#2E7D32", amber: "#B07A1E", red: "#B85638",
  blue: "#2F6BB0", violet: "#6B4FA0", lime: "#A2AF6E", med: "#7E8A86", teal: "#1F8A80",
};
const SCREEN_W = 1460;
const SCREEN_H = 820;

// ── diagram engine (boxes + arrows) ──────────────────────────────────────────
type Tone = "person" | "agent" | "work" | "data" | "report" | "clock";
const TONES: Record<Tone, { fill: string; stroke: string; text: string }> = {
  person: { fill: "#E4EDF7", stroke: "#2F6BB0", text: "#1E4E86" },
  agent:  { fill: "#E6F0E4", stroke: "#4E8C4A", text: "#2E5C2C" },
  work:   { fill: "#E0EEF3", stroke: "#2C7E9E", text: "#1B5268" },
  data:   { fill: "#F4ECD7", stroke: "#B07A1E", text: "#785111" },
  report: { fill: "#ECE7F4", stroke: "#6B4FA0", text: "#46356E" },
  clock:  { fill: "#EDECE9", stroke: "#8A8378", text: "#4B463E" },
};
type DBox = { x: number; y: number; w: number; h: number; label: string; sub?: string; tone: Tone };
type DArrow = { x1: number; y1: number; x2: number; y2: number; label?: string; dashed?: boolean; color?: string };
function Diagram({ boxes, arrows, vw = 560, vh = 300 }: { boxes: DBox[]; arrows: DArrow[]; vw?: number; vh?: number }) {
  return (
    <svg viewBox={`0 0 ${vw} ${vh}`} style={{ width: "100%", height: "auto", display: "block", fontFamily: FONT }}>
      <defs>
        <marker id="mx-ah" markerWidth="9" markerHeight="9" refX="6.5" refY="3" orient="auto">
          <path d="M0,0 L6.5,3 L0,6 Z" fill="#8A8378" />
        </marker>
      </defs>
      {arrows.map((a, i) => {
        const col = a.color || "#8A8378";
        return (
          <g key={`a${i}`}>
            <path d={`M${a.x1},${a.y1} L${a.x2},${a.y2}`} stroke={col} strokeWidth={1.8} fill="none" markerEnd="url(#mx-ah)" strokeDasharray={a.dashed ? "5 3" : undefined} />
            {a.label && (
              <text x={(a.x1 + a.x2) / 2} y={(a.y1 + a.y2) / 2 - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill={col} style={{ paintOrder: "stroke", stroke: "#FFFFFF", strokeWidth: 3 }}>{a.label}</text>
            )}
          </g>
        );
      })}
      {boxes.map((b, i) => {
        const c = TONES[b.tone];
        return (
          <g key={`b${i}`}>
            <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={11} fill={c.fill} stroke={c.stroke} strokeWidth={1.7} />
            <text x={b.x + b.w / 2} y={b.sub ? b.y + b.h / 2 - 6 : b.y + b.h / 2 + 1} textAnchor="middle" dominantBaseline="middle" fontSize={14} fontWeight={700} fill={c.text}>{b.label}</text>
            {b.sub && <text x={b.x + b.w / 2} y={b.y + b.h / 2 + 13} textAnchor="middle" dominantBaseline="middle" fontSize={11} fontWeight={500} fill={c.text} opacity={0.85}>{b.sub}</text>}
          </g>
        );
      })}
    </svg>
  );
}

// ── small UI atoms ───────────────────────────────────────────────────────────
function Dot({ c }: any) { return <span style={{ width: 8, height: 8, borderRadius: 999, background: c, display: "inline-block", flexShrink: 0 }} />; }
function Toggle({ on }: any) {
  return (
    <span style={{ width: 34, height: 20, borderRadius: 999, background: on ? A.green : "#CBD0D8", position: "relative", flexShrink: 0, display: "inline-block" }}>
      <span style={{ position: "absolute", top: 2, left: on ? 16 : 2, width: 16, height: 16, borderRadius: 999, background: "#FFF", boxShadow: "0 1px 2px rgba(0,0,0,0.3)" }} />
    </span>
  );
}
function Tile({ label, value, tone, note }: any) {
  return (
    <div style={{ flex: 1, background: A.panel, border: `1px solid ${A.border}`, borderRadius: 12, padding: "14px 16px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: A.ink3, letterSpacing: "0.02em" }}>{label}</div>
      <div style={{ marginTop: 6, fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: tone || A.ink }}>{value}</div>
      {note && <div style={{ marginTop: 2, fontSize: 11.5, fontWeight: 600, color: tone || A.ink3 }}>{note}</div>}
    </div>
  );
}
function Btn({ label, primary, color }: any) {
  const c = color || A.petrol;
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, padding: "9px 15px", borderRadius: 9, whiteSpace: "nowrap", color: primary ? "#FFF" : c, background: primary ? c : "transparent", border: primary ? "none" : `1.5px solid ${c}` }}>{label}</span>;
}
function Panel({ children, style }: any) {
  return <div style={{ background: A.panel, border: `1px solid ${A.border}`, borderRadius: 12, ...style }}>{children}</div>;
}
// a small "where this fact came from" provenance chip
function Src({ s, c }: any) {
  const col = c || A.ink3;
  return <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.03em", color: col, background: A.soft, border: `1px solid ${A.border}`, borderRadius: 999, padding: "2px 7px", whiteSpace: "nowrap" }}>{s}</span>;
}
function Pill({ t, c, bg }: any) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: c, background: bg, borderRadius: 999, padding: "4px 10px", whiteSpace: "nowrap" }}>{t}</span>;
}

// ── the always-on collaborative-agent rail (right of every operator screen) ──
// The per-role agent: it drafts, validates, flags — you approve. Talk or type.
function AgentRail({ msgs, name }: any) {
  const m = msgs || [{ from: "agent", text: "I'm your MAX agent. I draft, cross-check and flag — you always approve before anything changes." }];
  return (
    <div style={{ width: 320, background: A.navy, flexShrink: 0, display: "flex", flexDirection: "column", borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
      <div style={{ padding: "14px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 9 }}>
        <span style={{ width: 26, height: 26, borderRadius: 8, background: A.lime, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>✦</span>
        <div><div style={{ fontSize: 13.5, fontWeight: 700, color: "#FFF" }}>{name || "Your MAX agent"}</div><div style={{ fontSize: 11, color: "#8A93A6" }}>drafts · validates · flags · you approve</div></div>
      </div>
      <div style={{ flex: 1, padding: 14, display: "flex", flexDirection: "column", gap: 10, overflow: "hidden" }}>
        {m.map((b: any, i: number) => (
          <div key={i} style={{ display: "flex", justifyContent: b.from === "you" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: 250, fontSize: 12, lineHeight: 1.45, padding: "8px 11px", borderRadius: 11, background: b.from === "you" ? "rgba(255,255,255,0.12)" : "rgba(162,175,110,0.15)", color: b.from === "you" ? "#EAF0F8" : "#EAF0DC", border: b.from === "you" ? "none" : "1px solid rgba(162,175,110,0.3)" }}>
              {b.from !== "you" && <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", color: A.lime, marginBottom: 3 }}>AGENT</div>}
              {b.text}
              {b.did && <div style={{ marginTop: 7, fontSize: 10.5, fontWeight: 700, color: "#8FD3E6", display: "flex", alignItems: "center", gap: 5 }}>↳ {b.did}</div>}
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: 12, borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", gap: 8, alignItems: "center" }}>
        <div style={{ flex: 1, height: 34, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 9, display: "flex", alignItems: "center", padding: "0 12px", fontSize: 11.5, color: "#8A93A6" }}>Ask, or tell me to draft it…</div>
        <span style={{ width: 34, height: 34, borderRadius: 9, background: A.lime, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🎤</span>
      </div>
    </div>
  );
}

// ── operator app nav (the collaborative surface — grouped by the spine + role) ─
const SHELL_NAV: any[] = [
  { group: "THE SPINE" },
  { k: "snapshot", icon: "🧭", label: "Client Snapshot" },
  { k: "reconcile", icon: "⚖️", label: "Reconciliation" },
  { k: "enrich", icon: "✨", label: "Enrichment" },
  { group: "SALES" },
  { k: "briefing", icon: "📋", label: "Daily Briefing" },
  { k: "legal", icon: "📜", label: "Legal & Compliance" },
  { group: "OPERATIONS" },
  { k: "mission", icon: "🛡️", label: "Mission Plan" },
  { k: "pricing", icon: "🧮", label: "Trouble Matrix" },
  { k: "qi", icon: "🧾", label: "Quote ↔ Invoice" },
  { group: "MARKETING & CONSULTING" },
  { k: "mql", icon: "📈", label: "MQL & Attribution" },
  { k: "survey", icon: "📑", label: "Survey → Report" },
];
// system-admin console nav
const ADMIN_NAV: any[] = [
  { k: "connectors", icon: "🔌", label: "Connectors" },
  { k: "isolation", icon: "🔒", label: "Model & Box" },
  { k: "guardrails", icon: "🛟", label: "Guardrails" },
  { k: "audit", icon: "📜", label: "Audit" },
  { k: "users", icon: "👥", label: "Users" },
];
// business-admin console nav
const BIZ_NAV: any[] = [
  { k: "org", icon: "🗺️", label: "Org View" },
  { k: "approvals", icon: "✅", label: "Approvals" },
  { k: "attribution", icon: "🎯", label: "Attribution" },
  { k: "memory", icon: "🧠", label: "Org Memory" },
];

// ── operator shell (dark sidebar + topbar + agent rail) ──────────────────────
function Shell({ active, children, agent, role, name }: any) {
  return (
    <div style={{ display: "flex", height: SCREEN_H - 44 }}>
      <div style={{ width: 214, background: A.navy, padding: "16px 12px", display: "flex", flexDirection: "column", gap: 3, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "2px 8px 14px" }}>
          <span style={{ width: 22, height: 22, borderRadius: 6, background: A.petrol, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#FFF", fontSize: 12, fontWeight: 800 }}>M</span>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: "#FFF", letterSpacing: "-0.01em" }}>MAX Security</span>
        </div>
        {SHELL_NAV.map((n, ni) => {
          if (n.group) return <div key={`g${ni}`} style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.13em", color: "#5F6980", padding: "10px 10px 3px" }}>{n.group}</div>;
          const on = n.k === active;
          return (
            <div key={n.k} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", borderRadius: 8, background: on ? "rgba(43,85,96,0.35)" : "transparent", color: on ? "#FFF" : "#9BA3B4", fontSize: 12.5, fontWeight: on ? 700 : 500 }}>
              <span style={{ fontSize: 12.5, opacity: on ? 1 : 0.75 }}>{n.icon}</span>{n.label}
              {on && <span style={{ marginLeft: "auto", width: 5, height: 5, borderRadius: 999, background: A.lime }} />}
            </div>
          );
        })}
        <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 9, padding: "8px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <span style={{ width: 26, height: 26, borderRadius: 999, background: "#39435A", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#FFF" }}>{(name || "O")[0]}</span>
          <span style={{ fontSize: 12, color: "#9BA3B4" }}>{name || "Operator"} · {role || "MAX"}</span>
        </div>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", background: A.appBg, minWidth: 0 }}>
        <div style={{ height: 52, background: A.panel, borderBottom: `1px solid ${A.border}`, display: "flex", alignItems: "center", gap: 14, padding: "0 20px", flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: A.ink }}>{SHELL_NAV.find((n) => n.k === active)?.label}</div>
          {role && <span style={{ fontSize: 11, fontWeight: 700, color: A.petrol, background: "#E1EDEF", borderRadius: 999, padding: "3px 10px" }}>{role}</span>}
          <div style={{ marginLeft: "auto", width: 240, height: 32, background: A.soft, border: `1px solid ${A.border}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 8, padding: "0 10px", fontSize: 12.5, color: A.ink3 }}>🔍 Search clients, missions, people…</div>
          <span style={{ width: 30, height: 30, borderRadius: 999, background: "#39435A", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#FFF" }}>{(name || "O")[0]}</span>
        </div>
        <div style={{ flex: 1, padding: 20, overflow: "hidden", minHeight: 0 }}>{children}</div>
      </div>
      <AgentRail msgs={agent} name={name ? `${name}'s agent` : undefined} />
    </div>
  );
}

// ── admin/business-admin console shell (cyan, no agent rail) ──────────────────
function AdminShell({ active, children, nav, role, accent, name }: any) {
  const NAV = nav || ADMIN_NAV;
  const acc = accent || "#8FD3E6";
  return (
    <div style={{ display: "flex", height: SCREEN_H - 44 }}>
      <div style={{ width: 214, background: "#14171F", padding: "16px 12px", display: "flex", flexDirection: "column", gap: 3, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "2px 8px 6px" }}>
          <span style={{ fontSize: 17, color: acc }}>◆</span>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: "#FFF" }}>MAX · Tailormind</span>
        </div>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", color: acc, padding: "0 8px 12px" }}>{role || "SYSTEM ADMIN"}</div>
        {NAV.map((n: any) => {
          const on = n.k === active;
          return (
            <div key={n.k} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, background: on ? "rgba(143,211,230,0.16)" : "transparent", color: on ? "#FFF" : "#9BA3B4", fontSize: 13, fontWeight: on ? 700 : 500 }}>
              <span style={{ fontSize: 13, opacity: on ? 1 : 0.75 }}>{n.icon}</span>{n.label}
              {on && <span style={{ marginLeft: "auto", width: 5, height: 5, borderRadius: 999, background: acc }} />}
            </div>
          );
        })}
        <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 9, padding: "8px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <span style={{ width: 26, height: 26, borderRadius: 999, background: "#2B6E7E", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#FFF" }}>{(name || "A")[0]}</span>
          <span style={{ fontSize: 12, color: "#9BA3B4" }}>{name || "Admin"}</span>
        </div>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", background: A.appBg, minWidth: 0 }}>
        <div style={{ height: 52, background: A.panel, borderBottom: `1px solid ${A.border}`, display: "flex", alignItems: "center", gap: 14, padding: "0 20px", flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: A.ink }}>{NAV.find((n: any) => n.k === active)?.label}</div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#2B6E7E", background: "#E0EFF3", borderRadius: 999, padding: "3px 10px" }}>{role || "SYSTEM ADMIN"}</span>
          <div style={{ marginLeft: "auto", width: 30, height: 30, borderRadius: 999, background: "#2B6E7E", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#FFF" }}>{(name || "A")[0]}</div>
        </div>
        <div style={{ flex: 1, padding: 22, overflow: "hidden", minHeight: 0 }}>{children}</div>
      </div>
    </div>
  );
}

// ═══ THE SCREENS ═════════════════════════════════════════════════════════════
function ScreenBody({ screen }: any) {
  // ── CORE · Client Snapshot 360 ─────────────────────────────────────────────
  if (screen === "snapshot") {
    const timeline = [
      ["2017", "First engagement — exec protection, Geneva", "SAP", A.amber],
      ["2019", "Intelligence Portal subscription", "HubSpot", A.blue],
      ["2022", "Renewal + 3 missions (EMEA)", "Monday", A.violet],
      ["2024", "Contact moved — buyer left the company", "LinkedIn", A.teal],
      ["2026", "Re-engaged after Q3 threat report", "Firefly", A.green],
    ];
    const people = [
      ["D. Rosen", "Intelligence buyer", "Portal renewals", A.blue],
      ["A. Marchetti", "Operations contact", "Mission logistics", A.violet],
      ["S. Kaufman", "C-level sponsor", "Budget authority", A.petrol],
    ];
    return (
      <Shell active="snapshot" role="AE" name="Ori" agent={[
        { from: "agent", text: "Assembled Aflame Security from 7 systems. One record, one truth — nothing changed without you." },
        { from: "you", text: "who's the current buyer?" },
        { from: "agent", text: "D. Rosen (intel). The 2019 buyer left in 2024 — I flagged it in Reconciliation.", did: "opened People" },
      ]}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "100%" }}>
          <Panel style={{ padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span style={{ width: 46, height: 46, borderRadius: 12, background: "#E1EDEF", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🏢</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 20, fontWeight: 800, color: A.ink, letterSpacing: "-0.02em" }}>Aflame Security Ltd</span>
                  <Pill t="✓ reconciled · 1 record" c={A.green} bg="#E3F1E4" />
                  <Pill t="synced 4m ago · 96% confidence" c={A.ink3} bg={A.soft} />
                </div>
                <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: A.ink2 }}>Also known as:</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: A.ink }}>“Aflam”</span><Src s="HubSpot" c={A.blue} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: A.ink }}>“Aflame”</span><Src s="Monday" c={A.violet} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: A.ink }}>“Aflame Security Ltd”</span><Src s="SAP invoice" c={A.amber} />
                </div>
              </div>
            </div>
            <div style={{ marginTop: 14, display: "flex", gap: 12 }}>
              <Tile label="Lifetime value" value="$1.24M" tone={A.green} note="12 missions · SAP" />
              <Tile label="Open deals" value="2" note="Portal renewal · EP retainer" />
              <Tile label="Last contact" value="6 days" note="Firefly · webinar follow-up" />
              <Tile label="Intel risk" value="Elevated" tone={A.amber} note="Scout · EMEA" />
            </div>
          </Panel>
          <div style={{ display: "flex", gap: 14, flex: 1, minHeight: 0 }}>
            <Panel style={{ flex: 1.1, padding: 16, display: "flex", flexDirection: "column", minHeight: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 12 }}>Lifecycle — one entity, lead → account → client → mission</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 0, overflow: "hidden" }}>
                {timeline.map((t, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", paddingBottom: 13 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 999, background: t[3] as string, marginTop: 3 }} />
                      {i < timeline.length - 1 && <span style={{ width: 2, flex: 1, minHeight: 26, background: A.border }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 800, color: A.ink }}>{t[0]}</span>
                        <Src s={t[2] as string} c={t[3] as string} />
                      </div>
                      <div style={{ fontSize: 12.5, color: A.ink2, marginTop: 2 }}>{t[1]}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
            <Panel style={{ flex: 1, padding: 16 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 12 }}>People — one account, three personas</div>
              {people.map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 0", borderBottom: i < 2 ? `1px solid ${A.border}` : "none" }}>
                  <span style={{ width: 30, height: 30, borderRadius: 999, background: "#E1EDEF", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: A.petrol }}>{(p[0] as string).slice(0, 1)}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: A.ink }}>{p[0]}</div>
                    <div style={{ fontSize: 11.5, color: A.ink3 }}>{p[1]} · {p[2]}</div>
                  </div>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: p[3] as string }} />
                </div>
              ))}
              <div style={{ marginTop: 12, fontSize: 11, color: A.ink3 }}>Each persona needs a different meeting-prep — the agent tailors it.</div>
            </Panel>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 14, minHeight: 0 }}>
              <Panel style={{ padding: 14, borderLeft: `3px solid ${A.petrol}` }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: A.petrol, display: "flex", alignItems: "center", gap: 6 }}>SCOUT INTEL <Src s="on-prem" c={A.petrol} /></div>
                <div style={{ fontSize: 12, color: A.ink2, marginTop: 7, lineHeight: 1.45 }}>Active EMEA advisory touching two mission countries. Portal read pulled into the record automatically.</div>
              </Panel>
              <Panel style={{ padding: 14, borderLeft: `3px solid ${A.green}` }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: A.green, display: "flex", alignItems: "center", gap: 6 }}>WHATSAPP INTEL <Src s="captured" c={A.green} /></div>
                <div style={{ fontSize: 12, color: A.ink2, marginTop: 7, lineHeight: 1.45 }}>Budget cycle closes in March (thread w/ S. Kaufman). Competitor quote mentioned — pulled from the gold-that-was-connected-to-nothing.</div>
              </Panel>
              <Panel style={{ padding: 14, flex: 1 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: A.ink3 }}>COMMERCIALS · SAP</div>
                <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", fontSize: 12.5 }}><span style={{ color: A.ink2 }}>Retainer</span><span style={{ fontWeight: 700, color: A.ink }}>$18k / mo</span></div>
                <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", fontSize: 12.5 }}><span style={{ color: A.ink2 }}>Last mission</span><span style={{ fontWeight: 700, color: A.ink }}>$61k</span></div>
                <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", fontSize: 12.5 }}><span style={{ color: A.ink2 }}>Open invoice</span><span style={{ fontWeight: 700, color: A.amber }}>$61k · 12d overdue</span></div>
                <div style={{ marginTop: 12, background: "#EFF5EF", borderRadius: 10, padding: "10px 12px" }}>
                  <div style={{ fontSize: 9.5, fontWeight: 800, color: A.green, letterSpacing: "0.05em" }}>NEXT BEST ACTION</div>
                  <div style={{ fontSize: 11.5, color: A.ink2, marginTop: 4, lineHeight: 1.4 }}>Renewal + overdue both open — draft one combined outreach to S. Kaufman before the March budget close.</div>
                </div>
              </Panel>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // ── CORE · Reconciliation — "Sources disagree" (the signature screen) ───────
  if (screen === "reconcile") {
    const cols = [
      { src: "HubSpot", c: A.blue, rows: [["Name", "Aflam", false], ["Owner", "D. Rosen (left Apr '24)", true], ["Deal value", "$82,000", true], ["Status", "Negotiation", false], ["Last activity", "Firefly note, not logged", true]] },
      { src: "Monday", c: A.violet, rows: [["Name", "Aflame", false], ["Owner", "A. Marchetti", true], ["Deal value", "$61,000", true], ["Status", "Mission delivered", false], ["Last activity", "6 days ago", false]] },
      { src: "SAP invoice", c: A.amber, rows: [["Name", "Aflame Security Ltd", false], ["Owner", "—", false], ["Invoiced", "$61,000", true], ["Status", "Overdue 12d", true], ["Last activity", "Invoice sent", false]] },
    ];
    const golden = [["Name", "Aflame Security Ltd", "SAP · legal"], ["Owner", "A. Marchetti", "Monday · current"], ["Deal value", "$61,000", "SAP · invoiced"], ["Status", "Mission delivered", "Monday · latest"], ["Last activity", "6 days ago", "Monday"]];
    return (
      <Shell active="reconcile" role="AE" name="Ori" agent={[
        { from: "agent", text: "180 records need review across the DB. This one: 3 systems, 3 names — matched on domain + phone, 96%." },
        { from: "agent", text: "3 fields disagree. I propose a golden record but will NOT write until you decide.", did: "held write-back" },
      ]}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
          <Panel style={{ padding: "9px 16px", display: "flex", alignItems: "center", gap: 16 }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: A.ink3, letterSpacing: "0.06em" }}>REVIEW QUEUE</span>
            {[["2,300", "matched", A.ink], ["180", "need review", A.amber], ["47", "high-controversy", A.red]].map((q: any, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 16, fontWeight: 800, color: q[2] }}>{q[0]}</span><span style={{ fontSize: 11.5, color: A.ink3 }}>{q[1]}</span>
              </span>
            ))}
            <span style={{ marginLeft: "auto", fontSize: 11.5, color: A.ink3, fontStyle: "italic" }}>reviewing 1 of 180 · Aflame Security</span>
          </Panel>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: A.ink, letterSpacing: "-0.02em" }}>Sources disagree — Aflame Security</div>
              <div style={{ fontSize: 12, color: A.ink2, marginTop: 2 }}>Three systems, one company. Read the story, then approve before anything is written.</div>
            </div>
            <Pill t="match 96%" c={A.green} bg="#E3F1E4" />
            <Pill t="⚠ 3 need validation" c={A.red} bg="#F7E6E0" />
          </div>
          <div style={{ display: "flex", gap: 12, flex: 1, minHeight: 0 }}>
            <div style={{ flex: 2, display: "flex", gap: 8 }}>
              {cols.map((col, ci) => (
                <Panel key={ci} style={{ flex: 1, padding: 0, overflow: "hidden" }}>
                  <div style={{ padding: "9px 12px", borderBottom: `1px solid ${A.border}`, display: "flex", alignItems: "center", gap: 7 }}>
                    <Dot c={col.c} /><span style={{ fontSize: 12, fontWeight: 800, color: col.c }}>{col.src}</span>
                  </div>
                  {col.rows.map((r: any, ri: number) => (
                    <div key={ri} style={{ padding: "9px 12px", borderBottom: ri < col.rows.length - 1 ? `1px solid ${A.border}` : "none", background: r[2] ? "#FBF0EC" : "transparent" }}>
                      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.04em", color: A.ink3 }}>{r[0]}</div>
                      <div style={{ fontSize: 12, fontWeight: r[2] ? 700 : 500, color: r[2] ? A.red : A.ink, marginTop: 2 }}>{r[1]}</div>
                    </div>
                  ))}
                </Panel>
              ))}
              <Panel style={{ flex: 1, padding: 0, overflow: "hidden", border: `2px solid ${A.green}` }}>
                <div style={{ padding: "9px 12px", borderBottom: `1px solid ${A.border}`, background: "#EFF6EF", display: "flex", alignItems: "center", gap: 7 }}>
                  <span style={{ color: A.green, fontWeight: 800, fontSize: 13 }}>★</span><span style={{ fontSize: 12, fontWeight: 800, color: A.green }}>Golden record</span><span style={{ fontSize: 9.5, color: A.ink3, marginLeft: "auto" }}>→ what we'll write</span>
                </div>
                {golden.map((r: any, ri: number) => (
                  <div key={ri} style={{ padding: "9px 12px", borderBottom: ri < golden.length - 1 ? `1px solid ${A.border}` : "none" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.04em", color: A.ink3 }}>{r[0]}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: A.ink, marginTop: 2 }}>{r[1]}</div>
                    <div style={{ fontSize: 9, color: A.green, fontWeight: 700, marginTop: 1 }}>← {r[2]}</div>
                  </div>
                ))}
              </Panel>
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10, minHeight: 0 }}>
              <Panel style={{ padding: 13, borderLeft: `3px solid ${A.petrol}` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: A.petrol }}>WHAT THE AGENT DID — THE STORY</div>
                <div style={{ fontSize: 12, color: A.ink2, marginTop: 7, lineHeight: 1.45 }}>
                  Matched 3 records to <b style={{ color: A.ink }}>one company</b> on shared domain + phone. HubSpot's owner <b style={{ color: A.ink }}>left Apr 2024</b>; deal value differs <b style={{ color: A.red }}>$82k vs $61k</b> — HubSpot holds the old negotiation figure, SAP invoiced the delivered <b style={{ color: A.ink }}>$61k</b>. I propose $61k and flag the gap.
                </div>
              </Panel>
              <Panel style={{ padding: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: A.ink, marginBottom: 8 }}>Survivorship rules — why each winner</div>
                {[["Owner", "Monday — HubSpot's left"], ["Financials", "SAP — invoiced actual"], ["Name", "Legal name — for contracts"], ["Status", "Most-recent event wins"]].map((r: any, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11, padding: "5px 0", borderBottom: i < 3 ? `1px solid ${A.border}` : "none" }}>
                    <span style={{ fontWeight: 700, color: A.ink }}>{r[0]}</span><span style={{ color: A.ink3, textAlign: "right" }}>{r[1]}</span>
                  </div>
                ))}
                <div style={{ marginTop: 8, fontSize: 10.5, color: A.ink3, background: A.soft, borderRadius: 8, padding: "7px 9px" }}>Match key: domain <b>aflame-sec.com</b> · phone <b>+41 22 700 88</b> · name fuzzy <b>0.91</b></div>
              </Panel>
              <Panel style={{ padding: 12, flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: A.ink, marginBottom: 8 }}>Contradictions to resolve</div>
                {[["Owner mismatch", "use Monday's A. Marchetti", A.amber], ["Deal value 26%", "$82k vs $61k invoiced", A.red], ["Quote↔invoice variance", "to Ops reconciliation", A.amber]].map((f: any, i) => (
                  <div key={i} style={{ display: "flex", gap: 9, padding: "7px 0", borderBottom: i < 2 ? `1px solid ${A.border}` : "none" }}>
                    <span style={{ marginTop: 3, width: 7, height: 7, borderRadius: 999, background: f[2], flexShrink: 0 }} />
                    <div><div style={{ fontSize: 12, fontWeight: 700, color: A.ink }}>{f[0]}</div><div style={{ fontSize: 11, color: A.ink3 }}>{f[1]}</div></div>
                  </div>
                ))}
              </Panel>
            </div>
          </div>
          <Panel style={{ padding: "12px 18px", display: "flex", alignItems: "center", gap: 16, borderTop: `3px solid ${A.green}` }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: A.ink }}>Nothing is written until you approve. <span style={{ color: A.ink3, fontWeight: 500 }}>· reversible · logged to audit</span></div>
              <div style={{ fontSize: 11.5, color: A.ink3 }}>Approve writes the golden record into HubSpot + Monday. Edit overrides any field. Reject keeps all three untouched.</div>
            </div>
            <Btn label="Reject" color={A.ink3} />
            <Btn label="Edit fields" color={A.petrol} />
            <Btn primary color={A.green} label="✓ Approve & write back" />
          </Panel>
        </div>
      </Shell>
    );
  }

  // ── CORE · Enrichment at capture ───────────────────────────────────────────
  if (screen === "enrich") {
    return (
      <Shell active="enrich" role="Marketing" name="Odeia" agent={[
        { from: "agent", text: "New lead from the Q3 threat report. Enriched in 4s — person + city, scored, and tiered per product." },
      ]}>
        <div style={{ display: "flex", gap: 16, height: "100%" }}>
          <div style={{ flex: 1.1, display: "flex", flexDirection: "column", gap: 14, minHeight: 0 }}>
            <Panel style={{ padding: 18 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: A.ink3 }}>NEW LEAD · CAPTURED 12:04</div>
              <div style={{ marginTop: 6, fontSize: 18, fontWeight: 800, color: A.ink }}>j.weber@northwind-energy.com</div>
              <div style={{ fontSize: 12.5, color: A.ink2, marginTop: 2 }}>Source: downloaded “Q3 Threat Landscape” report · corporate email only, nothing else</div>
            </Panel>
            <Panel style={{ padding: 18, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink }}>Waterfall enrichment</div>
                <div style={{ fontSize: 10.5, color: A.ink3 }}>fall through providers — first hit wins</div>
              </div>
              {[["Clearbit", "no match on the personal domain", "✗", A.red], ["Apollo", "Julia Weber · VP Security Operations", "✓", A.green], ["LinkedIn", "Northwind Energy · 4,200 staff · Frankfurt + city", "✓", A.green], ["Verified email", "j.weber@northwind-energy.com · valid", "✓", A.green], ["Intent · 6sense", "3rd report this quarter — warming", "◐", A.amber]].map((s: any, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderBottom: i < 4 ? `1px solid ${A.border}` : "none", opacity: s[2] === "✗" ? 0.7 : 1 }}>
                  <span style={{ width: 22, height: 22, borderRadius: 999, background: s[3] === A.green ? "#E3F1E4" : s[3] === A.red ? "#F7E6E0" : "#F4ECD7", color: s[3], display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800 }}>{s[2]}</span>
                  <div style={{ width: 96, fontSize: 11.5, fontWeight: 700, color: A.ink3 }}>{s[0]}</div>
                  <div style={{ flex: 1, fontSize: 12.5, color: s[2] === "✗" ? A.ink3 : A.ink }}>{s[1]}{s[2] === "✗" && <span style={{ color: A.amber, fontWeight: 700 }}> → fell through to Apollo</span>}</div>
                </div>
              ))}
            </Panel>
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 14, minHeight: 0 }}>
            <Panel style={{ padding: 18 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink }}>MQL score</div>
                <div style={{ fontSize: 10.5, color: A.ink3 }}>per-lead, at capture</div>
              </div>
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ fontSize: 40, fontWeight: 800, color: A.green, letterSpacing: "-0.03em" }}>78</div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
                  {[["Seniority", 88, A.green], ["Intent", 72, A.amber], ["Fit", 74, A.green]].map((s: any, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 54, fontSize: 10.5, fontWeight: 700, color: A.ink3 }}>{s[0]}</span>
                      <div style={{ flex: 1, height: 7, borderRadius: 999, background: A.soft, overflow: "hidden" }}><div style={{ width: `${s[1]}%`, height: "100%", background: s[2] }} /></div>
                      <span style={{ width: 20, fontSize: 10.5, fontWeight: 700, color: A.ink }}>{s[1]}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ fontSize: 11, color: A.ink3, marginTop: 9 }}>MQL threshold = 60 → <b style={{ color: A.green }}>qualified</b>. Sub-scores make the number explainable, not a black box.</div>
            </Panel>
            <Panel style={{ padding: 18, flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 12 }}>Per-product qualification</div>
              {[["Academy (~$2k)", "Qualified — seniority + interest fit", A.green, "Route to self-serve nurture"], ["Intelligence Portal (~$80k)", "Not yet — no budget signal", A.amber, "Assign to AE for discovery"], ["Protective Ops", "Cold — no trigger", A.med, "Hold"]].map((p: any, i) => (
                <div key={i} style={{ padding: "11px 0", borderBottom: i < 2 ? `1px solid ${A.border}` : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: A.ink }}>{p[0]}</span>
                    <Pill t={p[1]} c={p[2]} bg={p[2] === A.green ? "#E3F1E4" : p[2] === A.amber ? "#F4ECD7" : A.soft} />
                  </div>
                  <div style={{ fontSize: 11.5, color: A.ink3, marginTop: 4 }}>→ {p[3]}</div>
                </div>
              ))}
              <div style={{ marginTop: 12, background: "#E1EDEF", borderRadius: 10, padding: "11px 13px", fontSize: 12, color: A.petrol, fontWeight: 600 }}>Decision: assign to AE (Ori) for the Portal tier. No more “one qualified flag” — the tier drives the routing.</div>
            </Panel>
          </div>
        </div>
      </Shell>
    );
  }

  // ── SALES · Daily Briefing ─────────────────────────────────────────────────
  if (screen === "briefing") {
    const leads = [
      ["Northwind Energy", "New CISO · 3rd report DL", 92, A.green, "New decision-maker + intent — perfect timing for the Portal."],
      ["Halcyon Bank", "Contact moved Siemens → BMW", 88, A.green, "Known warm contact now at a non-client. Fresh opportunity."],
      ["Meridian Air", "Renewal window in 30d", 81, A.amber, "Portal renewal approaching; last touch 40 days ago."],
    ];
    return (
      <Shell active="briefing" role="AE" name="Ori" agent={[
        { from: "agent", text: "45,000 → 10 for today. I drafted the first touch for each — you send, I don't." },
        { from: "you", text: "why Halcyon?" },
        { from: "agent", text: "Your contact moved from Siemens (client) to BMW (non-client) — a fresh door.", did: "explained rank" },
      ]}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "100%" }}>
          <div>
            <div style={{ fontSize: 19, fontWeight: 800, color: A.ink, letterSpacing: "-0.02em" }}>Your 10 for today</div>
            <div style={{ fontSize: 12.5, color: A.ink2, marginTop: 2 }}>The database is the database. The agent read all 45,000 overnight and hands you the ten that matter — with a draft.</div>
          </div>
          <Panel style={{ padding: "14px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, color: A.ink2, flexWrap: "wrap" }}>
              {[["45,000", "contacts"], ["→", ""], ["2,300", "in territory"], ["→", ""], ["340", "warm signals"], ["→", ""], ["10", "today"]].map((s: any, i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                  {s[0] === "→" ? <span style={{ color: A.ink3 }}>→</span> : <span style={{ fontSize: 15, fontWeight: 800, color: i === 6 ? A.green : A.ink }}>{s[0]}</span>}
                  {s[1] && <span style={{ fontSize: 11.5, color: A.ink3 }}>{s[1]}</span>}
                </span>
              ))}
              <span style={{ marginLeft: "auto", fontSize: 11.5, color: A.ink3, fontStyle: "italic" }}>SQL narrows · the agent only ever synthesizes the shortlist</span>
            </div>
          </Panel>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, minHeight: 0, overflow: "hidden" }}>
            {leads.map((l: any, i) => (
              <Panel key={i} style={{ padding: 16, display: "flex", gap: 16, alignItems: "center" }}>
                <div style={{ width: 44, textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: l[3], letterSpacing: "-0.02em" }}>{l[2]}</div>
                  <div style={{ fontSize: 9.5, fontWeight: 700, color: A.ink3 }}>SCORE</div>
                </div>
                <div style={{ width: 210 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: A.ink }}>{l[0]}</div>
                  <Pill t={l[1]} c={l[3]} bg={l[3] === A.green ? "#E3F1E4" : "#F4ECD7"} />
                </div>
                <div style={{ flex: 1, fontSize: 12, color: A.ink2, lineHeight: 1.4 }}>{l[4]}</div>
                <div style={{ width: 300, background: A.soft, border: `1px solid ${A.border}`, borderRadius: 10, padding: "10px 12px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: A.ink3 }}>DRAFTED FIRST TOUCH — in your voice</div>
                  <div style={{ fontSize: 11.5, color: A.ink2, marginTop: 4, lineHeight: 1.4 }}>“Hi {String(l[0]).split(" ")[0]} — saw your team pulling our threat reporting…”</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}><Btn primary color={A.petrol} label="Approve & send" /><Btn label="Edit" color={A.ink3} /></div>
              </Panel>
            ))}
          </div>
        </div>
      </Shell>
    );
  }

  // ── SALES · Legal & Compliance review ──────────────────────────────────────
  if (screen === "legal") {
    return (
      <Shell active="legal" role="Legal ops" name="Ori" agent={[
        { from: "agent", text: "New vendor security addendum from Northwind — 8 questions, 7 we've answered before. Drafted from the clause corpus." },
        { from: "agent", text: "1 is new (data-residency in DE). Flagged for a human — I don't guess on novel legal.", did: "flagged 1 for review" },
      ]}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "100%" }}>
          <div><div style={{ fontSize: 19, fontWeight: 800, color: A.ink }}>Legal & Compliance review</div>
            <div style={{ fontSize: 12.5, color: A.ink2, marginTop: 2 }}>~30 reviews a month, ~90% repeating questions. The agent answers the repeats from the clause corpus and flags only what's genuinely new — the manager approves.</div></div>
          <div style={{ display: "flex", gap: 12 }}>
            <Tile label="Reviews / mo" value="30" />
            <Tile label="Repeating Qs" value="90%" tone={A.green} note="answered from corpus" />
            <Tile label="Genuinely new" value="10–15%" tone={A.amber} note="→ human" />
            <Tile label="Legal time saved" value="~50%" tone={A.green} note="est." />
          </div>
          <div style={{ display: "flex", gap: 14, flex: 1, minHeight: 0 }}>
            <Panel style={{ flex: 1, padding: 16 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: A.ink3 }}>DOCUMENT · Northwind vendor security addendum</div>
              <div style={{ marginTop: 8, fontSize: 13.5, fontWeight: 700, color: A.ink, marginBottom: 4 }}>8 questions detected</div>
              {[["Data encryption at rest?", "repeat", A.green], ["Sub-processor list?", "repeat", A.green], ["Breach notification SLA?", "repeat", A.green], ["SOC 2 / ISO 27001?", "repeat", A.green], ["Data residency — Germany?", "NEW", A.red], ["Right to audit?", "repeat", A.green], ["Retention period?", "repeat", A.green], ["Liability cap?", "check", A.amber]].map((q: any, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: i < 7 ? `1px solid ${A.border}` : "none" }}>
                  <span style={{ fontSize: 12.5, color: A.ink }}>{q[0]}</span>
                  <Pill t={q[1]} c={q[2]} bg={q[2] === A.green ? "#E3F1E4" : q[2] === A.red ? "#F7E6E0" : "#F4ECD7"} />
                </div>
              ))}
            </Panel>
            <Panel style={{ flex: 1.1, padding: 16, display: "flex", flexDirection: "column", minHeight: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 10 }}>Drafted answers — cite the clause, never invent</div>
              {[["Data encryption at rest?", "Yes — AES-256, keys held in the customer KMS.", "Clause 4.2 · MSA", A.green], ["Breach notification SLA?", "72 hours, per our standard DPA.", "Clause 9.1 · DPA", A.green], ["Data residency — Germany?", "NEW — no standard clause on file. Needs legal.", "no match", A.red]].map((a: any, i) => (
                <div key={i} style={{ padding: "11px 0", borderBottom: i < 2 ? `1px solid ${A.border}` : "none" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: A.ink }}>{a[0]}</div>
                  <div style={{ fontSize: 12, color: a[3] === A.red ? A.red : A.ink2, marginTop: 3, lineHeight: 1.4 }}>{a[1]}</div>
                  <div style={{ marginTop: 5 }}><Src s={`↳ ${a[2]}`} c={a[3]} /></div>
                </div>
              ))}
              <div style={{ marginTop: "auto", paddingTop: 12, display: "flex", gap: 8 }}>
                <Btn label="Send new Q to legal" color={A.red} />
                <Btn primary color={A.green} label="✓ Approve 7 repeat answers" />
              </div>
            </Panel>
          </div>
        </div>
      </Shell>
    );
  }

  // ── OPERATIONS · Mission Plan Builder ──────────────────────────────────────
  if (screen === "mission") {
    return (
      <Shell active="mission" role="RM" name="Amir" agent={[
        { from: "agent", text: "Assembled MX-2214 from your parameters — driver, vehicle, timing all pulled in. 22 docs drafted." },
        { from: "agent", text: "Verification gate raised 2 flags before this can ship.", did: "held the plan" },
      ]}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div><div style={{ fontSize: 19, fontWeight: 800, color: A.ink }}>Mission MX-2214 · Executive protection · Geneva</div>
              <div style={{ fontSize: 12.5, color: A.ink2, marginTop: 2 }}>The RM used to be the integration layer — 4 managers, 2 operators, the portal. Now the plan assembles itself; the RM judges.</div></div>
            <Pill t="on-prem · isolated" c={A.petrol} bg="#E1EDEF" />
          </div>
          <div style={{ display: "flex", gap: 14, flex: 1, minHeight: 0 }}>
            <Panel style={{ flex: 1, padding: 16 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 12 }}>Elements — auto-pulled from parameters</div>
              {[["Driver", "M. Favre · vetted · EN/FR", "Vendor DB"], ["Vehicle", "Armored SUV · B6", "Vendor DB"], ["Timing", "3–6 Mar · airport → summit", "Client intake"], ["GSOC", "India desk · 24/7 watch", "Roster"], ["Route risk", "2 red zones flagged", "Scout"]].map((e: any, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: i < 4 ? `1px solid ${A.border}` : "none" }}>
                  <div style={{ flex: 1 }}><div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink }}>{e[0]}</div><div style={{ fontSize: 11.5, color: A.ink3 }}>{e[1]}</div></div>
                  <Src s={e[2]} />
                </div>
              ))}
            </Panel>
            <Panel style={{ flex: 1, padding: 16 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 12 }}>The 22 documents — drafted</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {["Activity clock", "Operational info", "Ops command", "Threat brief", "Task cards", "Mandate", "Comms plan", "Medevac", "Contact sheet", "Vehicle log"].map((d, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: A.ink2, padding: "6px 0" }}><span style={{ color: A.green, fontWeight: 800 }}>✓</span>{d}</div>
                ))}
              </div>
              <div style={{ marginTop: 10, fontSize: 11.5, color: A.ink3 }}>+12 more · all populated from the one record. You edit, you don't retype.</div>
            </Panel>
            <Panel style={{ flex: 1, padding: 16, borderLeft: `3px solid ${A.amber}` }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.amber, marginBottom: 12 }}>⚠ Verification gate — before it ships</div>
              {[["Scout wider-picture miss", "A G7 summit runs 20km across the border during your window — not reflected in the route plan.", A.red], ["Vehicle not confirmed", "Vendor hasn't acknowledged the B6 booking.", A.amber], ["Step skipped", "Medevac contact left blank.", A.amber]].map((f: any, i) => (
                <div key={i} style={{ padding: "10px 0", borderBottom: i < 2 ? `1px solid ${A.border}` : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: f[2] }} /><span style={{ fontSize: 12.5, fontWeight: 700, color: A.ink }}>{f[0]}</span></div>
                  <div style={{ fontSize: 11.5, color: A.ink3, marginTop: 3, paddingLeft: 15, lineHeight: 1.4 }}>{f[1]}</div>
                </div>
              ))}
              <div style={{ marginTop: 12 }}><Btn primary color={A.petrol} label="Resolve flags → send for approval" /></div>
            </Panel>
          </div>
        </div>
      </Shell>
    );
  }

  // ── OPERATIONS · Trouble/Travel Matrix pricing ─────────────────────────────
  if (screen === "pricing") {
    const topics = [["Political instability", "High", A.red], ["Crime", "Medium", A.amber], ["Terror threat", "High", A.red], ["Health / medical", "Low", A.green], ["Infrastructure", "Medium", A.amber], ["Civil unrest", "Elevated", A.amber], ["Border friction", "Critical", A.red], ["Comms / cyber", "Low", A.green]];
    return (
      <Shell active="pricing" role="RM" name="Eyal" agent={[
        { from: "agent", text: "Priced from your Trouble Matrix — 8 topics, 17 parameters. Border just closed, so I re-priced." },
      ]}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div><div style={{ fontSize: 19, fontWeight: 800, color: A.ink }}>Trouble Matrix — pricing engine</div>
              <div style={{ fontSize: 12.5, color: A.ink2, marginTop: 2 }}>Eyal's own model, 80% built, now wired to Scout. Risk → operational + commercial composition → price. Non-numbers people stop pricing by hand.</div></div>
            <Pill t="on-prem · isolated" c={A.petrol} bg="#E1EDEF" />
          </div>
          <div style={{ display: "flex", gap: 14, flex: 1, minHeight: 0 }}>
            <Panel style={{ flex: 1, padding: 16, display: "flex", flexDirection: "column", minHeight: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 4 }}>8 topics · 17 parameters → ~200</div>
              <div style={{ fontSize: 11, color: A.ink3, marginBottom: 10 }}>Risk levels synced from Scout — no hand-set guesses.</div>
              {topics.map((t: any, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${A.border}` }}>
                  <span style={{ fontSize: 12.5, color: A.ink }}>{t[0]}</span>
                  <Pill t={t[1]} c={t[2]} bg={t[2] === A.red ? "#F7E6E0" : t[2] === A.amber ? "#F4ECD7" : "#E3F1E4"} />
                </div>
              ))}
              <div style={{ marginTop: 10, background: A.soft, borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: A.petrol, letterSpacing: "0.04em" }}>BORDER FRICTION · 17 PARAMETERS</div>
                <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {["crossing time", "closure risk", "docs regime", "escort req.", "alt-route", "curfew", "checkpoint density"].map((p, i) => (
                    <span key={i} style={{ fontSize: 9.5, fontWeight: 600, color: A.ink2, background: "#FFF", border: `1px solid ${A.border}`, borderRadius: 999, padding: "2px 7px" }}>{p}</span>
                  ))}
                  <span style={{ fontSize: 9.5, fontWeight: 700, color: A.ink3 }}>+10 more</span>
                </div>
              </div>
            </Panel>
            <div style={{ flex: 1.2, display: "flex", flexDirection: "column", gap: 14, minHeight: 0 }}>
              <div style={{ background: "#0A1B3D", borderRadius: 12, padding: "18px 20px", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13, lineHeight: 1.9, color: "#DCE6F2", whiteSpace: "pre-line", flex: 1 }}>
                {"base_rate         = $8,200 / day\nrisk_multiplier   = 1.9   (matrix)\nemergency_factor  = ×2.5  (border closed)\ndays              = 4\n──────────────────────────────\nsubtotal          = $155,800\nVAT (22%)         = $34,276\ncurrency          = CHF → USD\n──────────────────────────────\nTOTAL             = $190,076"}
              </div>
              <Panel style={{ padding: 14, borderLeft: `3px solid ${A.amber}` }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: A.amber }}>⚡ DYNAMIC RE-PRICE</div>
                <div style={{ fontSize: 12, color: A.ink2, marginTop: 6, lineHeight: 1.45 }}>Border closed 2h ago → vendor cost +40% → emergency factor ×2.5 applied. Every number computed in code, never guessed by a model.</div>
              </Panel>
            </div>
            <Panel style={{ flex: 1, padding: 16, display: "flex", flexDirection: "column", minHeight: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 10 }}>Composition</div>
              {[["Operational", "2 PPO · 1 driver · armored B6 · GSOC", A.petrol], ["Commercial", "markup 75% · not the 250% a hand-calc once showed", A.green], ["Approval", "$190k → COO ( > $25k tier )", A.violet]].map((c: any, i) => (
                <div key={i} style={{ padding: "8px 0", borderBottom: i < 2 ? `1px solid ${A.border}` : "none" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: c[2] }}>{c[0]}</div>
                  <div style={{ fontSize: 11.5, color: A.ink2, marginTop: 3, lineHeight: 1.4 }}>{c[1]}</div>
                </div>
              ))}
              <div style={{ marginTop: 12, fontSize: 10.5, fontWeight: 800, color: A.ink3, letterSpacing: "0.04em" }}>VENDORS · 300 in DB (25–40 core)</div>
              <div style={{ marginTop: 6 }}>
                {[["Alpine Secure", "B6 SUV · Geneva", "$9,600", A.green], ["Léman Drivers", "vetted EN/FR", "$4,200", A.green], ["RhôneGuard", "backup · +12% war rate", "$5,100", A.amber]].map((v: any, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: i < 2 ? `1px solid ${A.border}` : "none" }}>
                    <span style={{ width: 7, height: 7, borderRadius: 999, background: v[3], flexShrink: 0 }} />
                    <div style={{ flex: 1 }}><div style={{ fontSize: 11.5, fontWeight: 700, color: A.ink }}>{v[0]}</div><div style={{ fontSize: 10, color: A.ink3 }}>{v[1]}</div></div>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: A.ink2 }}>{v[2]}</span>
                  </div>
                ))}
                <div style={{ marginTop: 6, fontSize: 10.5, color: A.ink3 }}>Agent picks cheapest vetted, flags war-rate spikes, drafts the negotiation ask.</div>
              </div>
              <div style={{ marginTop: 12 }}><Btn primary color={A.petrol} label="Send to COO for approval" /></div>
            </Panel>
          </div>
        </div>
      </Shell>
    );
  }

  // ── OPERATIONS · Quote ↔ Invoice reconciliation ────────────────────────────
  if (screen === "qi") {
    const lines = [["PPO team · 4 days", "$32,800", "$32,800", false], ["Armored vehicle", "$9,600", "$9,600", false], ["Overtime", "—", "+$5,400 (18h)", true], ["Added asset (day 3)", "—", "+$4,200", true], ["Expenses / lodging", "$3,100", "$4,850", true], ["VAT 22%", "$10,010", "$12,507", true]];
    return (
      <Shell active="qi" role="RM" name="Amir" agent={[
        { from: "agent", text: "Invoice is +27% over quote. I pulled the hours-on-ground from Monday and flagged every delta." },
      ]}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div><div style={{ fontSize: 19, fontWeight: 800, color: A.ink }}>Quote MX-2214 vs Invoice</div>
              <div style={{ fontSize: 12.5, color: A.ink2, marginTop: 2 }}>The 25–30% variance, rebuilt by hand no more. Copy-forward + hours-on-ground pulled in; every delta flagged for the manager.</div></div>
            <Pill t="+27% variance" c={A.red} bg="#F7E6E0" />
          </div>
          <div style={{ display: "flex", gap: 14, flex: 1, minHeight: 0 }}>
            <Panel style={{ flex: 1.5, padding: 0, overflow: "hidden" }}>
              <div style={{ display: "flex", padding: "10px 16px", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", color: A.ink3, borderBottom: `1px solid ${A.border}` }}>
                <span style={{ flex: 2 }}>LINE</span><span style={{ flex: 1, textAlign: "right" }}>QUOTE</span><span style={{ flex: 1.2, textAlign: "right" }}>INVOICE</span>
              </div>
              {lines.map((l: any, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", padding: "11px 16px", fontSize: 13, borderBottom: i < lines.length - 1 ? `1px solid ${A.border}` : "none", background: l[3] ? "#FBF0EC" : "transparent" }}>
                  <span style={{ flex: 2, color: A.ink, fontWeight: l[3] ? 700 : 500 }}>{l[0]}</span>
                  <span style={{ flex: 1, textAlign: "right", color: A.ink2 }}>{l[1]}</span>
                  <span style={{ flex: 1.2, textAlign: "right", color: l[3] ? A.red : A.ink2, fontWeight: l[3] ? 700 : 500 }}>{l[2]}</span>
                </div>
              ))}
              <div style={{ display: "flex", alignItems: "center", padding: "12px 16px", background: A.soft }}>
                <span style={{ flex: 2, fontWeight: 800, color: A.ink }}>Total</span>
                <span style={{ flex: 1, textAlign: "right", fontWeight: 700, color: A.ink2 }}>$55,510</span>
                <span style={{ flex: 1.2, textAlign: "right", fontWeight: 800, color: A.red }}>$70,357</span>
              </div>
            </Panel>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 14, minHeight: 0 }}>
              <Panel style={{ padding: 14, borderLeft: `3px solid ${A.petrol}` }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: A.petrol }}>ITINERARY DIFF · six-day plan resent</div>
                <div style={{ marginTop: 8, fontSize: 12, fontFamily: "ui-monospace, Menlo, monospace", lineHeight: 1.7 }}>
                  <div style={{ color: A.ink3 }}>Day 1–3 · unchanged</div>
                  <div style={{ background: "#FBF0EC", padding: "2px 6px", borderRadius: 6, color: A.red }}>Day 4 · pickup 08:00 → 06:30</div>
                  <div style={{ color: A.ink3 }}>Day 5–6 · unchanged</div>
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: A.ink3 }}>One line changed — you read the diff, not the whole itinerary.</div>
              </Panel>
              <Panel style={{ padding: 14, flex: 1 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: A.ink, marginBottom: 8 }}>Where the delta came from</div>
                {[["Overtime +18h", "from Monday hours box"], ["Added asset day 3", "mid-mission change log"], ["Expenses", "receipts, OneDrive"]].map((d: any, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "7px 0", borderBottom: i < 2 ? `1px solid ${A.border}` : "none" }}>
                    <span style={{ color: A.ink, fontWeight: 600 }}>{d[0]}</span><Src s={d[1]} />
                  </div>
                ))}
                <div style={{ marginTop: 12 }}><Btn primary color={A.green} label="✓ Approve invoice" /></div>
              </Panel>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // ── MARKETING · MQL & Attribution ──────────────────────────────────────────
  if (screen === "mql") {
    return (
      <Shell active="mql" role="Marketing" name="Odeia" agent={[
        { from: "agent", text: "Webinar dumped 340 leads to sales. I scored them, killed the 6-a-day blast, and closed the attribution loop." },
      ]}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "100%" }}>
          <div><div style={{ fontSize: 19, fontWeight: 800, color: A.ink }}>MQL & Attribution</div>
            <div style={{ fontSize: 12.5, color: A.ink2, marginTop: 2 }}>The marketing→sales seam, closed: a real MQL layer, segmentation, nurture, and a campaign→revenue chain that no longer goes dark.</div></div>
          <div style={{ display: "flex", gap: 14, flex: 1, minHeight: 0 }}>
            <Panel style={{ flex: 1, padding: 16 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 10 }}>The MQL gate</div>
              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <Tile label="New leads · 7d" value="340" />
                <Tile label="Passed MQL" value="86" tone={A.green} note="scored ≥ 60" />
                <Tile label="Nurtured" value="254" tone={A.amber} note="not yet ready" />
              </div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: A.ink3, marginBottom: 6 }}>SEGMENTATION — no more one-blast-to-everyone</div>
              {[["Aviation · DACH", "22", A.blue], ["Finance · UK", "31", A.violet], ["Energy · EMEA", "18", A.petrol], ["Suppressed (got 6/day)", "kill", A.red]].map((s: any, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: i < 3 ? `1px solid ${A.border}` : "none" }}>
                  <span style={{ fontSize: 12.5, color: A.ink }}>{s[0]}</span><Pill t={s[1]} c={s[2]} bg={s[2] === A.red ? "#F7E6E0" : A.soft} />
                </div>
              ))}
            </Panel>
            <Panel style={{ flex: 1, padding: 16, display: "flex", flexDirection: "column" }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink }}>Attribution — the full journey, not last-touch</div>
              <div style={{ fontSize: 11, color: A.ink3, marginBottom: 12 }}>Aggregate marketing view — the campaign→revenue chain that used to go dark.</div>
              <div style={{ background: A.soft, borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: A.ink3, marginBottom: 10 }}>NORTHWIND ENERGY · $180k closed</div>
                {[["Q3 Threat Report", "downloaded", A.blue, "Jul"], ["Security webinar", "attended", A.violet, "Aug"], ["LinkedIn touch", "AE outreach", A.petrol, "Sep"], ["Closed — Portal", "$180k", A.green, "Oct"]].map((t: any, i, arr) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <span style={{ width: 11, height: 11, borderRadius: 999, background: t[2], boxShadow: `0 0 0 2px #FFF, 0 0 0 3px ${t[2]}`, marginTop: 3 }} />
                      {i < arr.length - 1 && <span style={{ width: 2, height: 20, background: A.border }} />}
                    </div>
                    <div style={{ flex: 1, paddingBottom: i < arr.length - 1 ? 6 : 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: A.ink }}>{t[0]} <span style={{ fontSize: 10.5, fontWeight: 500, color: A.ink3 }}>· {t[3]}</span></div>
                      <div style={{ fontSize: 11, color: t[2] }}>{t[1]}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, background: "#E1EDEF", borderRadius: 10, padding: "10px 12px", fontSize: 11.5, color: A.petrol, lineHeight: 1.45 }}>The Q3 report <b>started</b> a journey that closed <b>$180k</b> three touches later — GA ↔ HubSpot joined, so the whole path is credited, not just the last click.</div>
              <div style={{ marginTop: "auto", paddingTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 11, color: A.ink3, flex: 1 }}>Agent drafted next week's nurture for the 254 not-yet-ready.</span>
                <Btn primary color={A.petrol} label="Review & approve nurture" />
              </div>
            </Panel>
          </div>
          <Panel style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 16, flexShrink: 0, borderLeft: `3px solid ${A.violet}` }}>
            <div style={{ flexShrink: 0, width: 196 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: A.violet, letterSpacing: "0.04em" }}>CROSS-FUNCTIONAL COORDINATION</div>
              <div style={{ fontSize: 11, color: A.ink3, marginTop: 3, lineHeight: 1.35 }}>Intel + Marketing share one launch calendar — no webinar goes out unilaterally, no one's looped in late.</div>
            </div>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
              {[["Intel proposes", "Scout", A.petrol], ["Marketing brief auto-drafted", "shared calendar", A.violet], ["Both approve", "before launch", A.green]].map((s: any, i: number, arr: any[]) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "inline-flex", flexDirection: "column", background: A.soft, border: `1px solid ${A.border}`, borderRadius: 9, padding: "7px 11px" }}>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: A.ink }}>{s[0]}</span>
                    <span style={{ fontSize: 9.5, fontWeight: 600, color: s[2] }}>{s[1]}</span>
                  </span>
                  {i < arr.length - 1 && <span style={{ color: A.ink3, fontSize: 13 }}>→</span>}
                </span>
              ))}
            </div>
            <div style={{ flexShrink: 0, maxWidth: 300, background: "#F1EEF7", borderRadius: 10, padding: "9px 13px" }}>
              <div style={{ fontSize: 9.5, fontWeight: 800, color: A.violet, letterSpacing: "0.04em" }}>NEXT ON THE CALENDAR</div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: A.ink, marginTop: 3 }}>Webinar: G7 Threat Briefing · Mar 6</div>
              <div style={{ fontSize: 10.5, color: A.ink3, marginTop: 1 }}>Intel proposed → brief drafted → awaiting both sign-offs</div>
            </div>
          </Panel>
        </div>
      </Shell>
    );
  }

  // ── CONSULTING · Survey → Report ───────────────────────────────────────────
  if (screen === "survey") {
    return (
      <Shell active="survey" role="Consulting" name="Iran" agent={[
        { from: "agent", text: "Scored the site against 40 benchmark surveys and drafted the report. You bring the judgment; I bring the first draft." },
      ]}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div><div style={{ fontSize: 19, fontWeight: 800, color: A.ink }}>Survey → Report engine</div>
              <div style={{ fontSize: 12.5, color: A.ink2, marginTop: 2 }}>The report that took several days, drafted in minutes. Benchmark DB scores the new client; the consultant supplies site-specific judgment.</div></div>
            <Pill t="Phase 2+ · deferred (tiny volume)" c={A.med} bg={A.soft} />
          </div>
          <div style={{ display: "flex", gap: 14, flex: 1, minHeight: 0 }}>
            <Panel style={{ flex: 1, padding: 16 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 12 }}>The pipeline</div>
              {[["Intake", "questionnaire + blueprints + OSINT", "✓"], ["Risk matrix", "40 benchmark surveys scored this site", "✓"], ["Gaps", "perimeter · access control · night cover", "✓"], ["Recommendations", "immediate / medium / long", "✓"], ["Report draft", "~10pg, ready for your judgment", "◐"]].map((s: any, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 11, padding: "11px 0", borderBottom: i < 4 ? `1px solid ${A.border}` : "none" }}>
                  <span style={{ width: 22, height: 22, borderRadius: 999, background: s[2] === "✓" ? "#E3F1E4" : "#F4ECD7", color: s[2] === "✓" ? A.green : A.amber, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800 }}>{s[2]}</span>
                  <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 700, color: A.ink }}>{s[0]}</div><div style={{ fontSize: 11.5, color: A.ink3 }}>{s[1]}</div></div>
                </div>
              ))}
            </Panel>
            <Panel style={{ flex: 1.1, padding: 16, background: "#0E1524" }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: A.lime, letterSpacing: "0.06em" }}>REPORT PREVIEW — “HOW THE ADVERSARY SEES YOU”</div>
              <div style={{ marginTop: 12, background: "#182338", borderRadius: 10, padding: 16, height: "82%", display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#FFF" }}>Northwind HQ · Frankfurt</div>
                <div style={{ fontSize: 11.5, color: "#9BB0C9" }}>Executive risk exposure · 10 pages</div>
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  {[["Perimeter", A.red], ["Access", A.amber], ["Night", A.red], ["Cyber-phys", A.green]].map((r: any, i) => (
                    <div key={i} style={{ flex: 1, background: "rgba(255,255,255,0.06)", borderRadius: 8, padding: "9px 8px", textAlign: "center" }}>
                      <div style={{ width: 10, height: 10, borderRadius: 999, background: r[1], margin: "0 auto 6px" }} />
                      <div style={{ fontSize: 10, color: "#C7D3E2" }}>{r[0]}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 8, fontSize: 11.5, color: "#9BB0C9", lineHeight: 1.5 }}>A visual, adversary's-eye view — not a boring page with a photo. The client viscerally sees the exposure, then the fix.</div>
                <div style={{ marginTop: "auto" }}><Btn primary color={A.lime} label="Open draft for editing" /></div>
              </div>
            </Panel>
          </div>
        </div>
      </Shell>
    );
  }

  // ── SYSTEM ADMIN · Connectors ──────────────────────────────────────────────
  if (screen === "connectors") {
    const conns = [
      ["🟠", "HubSpot", "marketing DB + sales pipeline · 45,000 contacts", A.green, "Connected", "2m ago", "read ⇄ write"],
      ["🔵", "Monday", "ops + CS + event leads", A.green, "Connected", "2m ago", "read ⇄ write"],
      ["🎙️", "Firefly", "meeting notes — writes to Notes, we re-route to the log", A.amber, "Config fix", "5m ago", "read-only"],
      ["📧", "Outlook / M365", "email + SharePoint client folders", A.green, "Connected", "1m ago", "read-only"],
      ["🛰️", "Scout portal", "intelligence — read into every plan", A.green, "Connected", "3m ago", "read-only"],
      ["🧾", "SAP", "invoicing + mission codes (org memory)", A.green, "Connected", "8m ago", "read-only"],
      ["💬", "WhatsApp", "client + intel threads — the gold, now captured", A.amber, "Pilot", "12m ago", "read-only"],
    ];
    return (
      <AdminShell active="connectors" role="SYSTEM ADMIN" name="GoSystems">
        <div style={{ display: "flex", flexDirection: "column", gap: 16, height: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div><div style={{ fontSize: 20, fontWeight: 700, color: A.ink }}>Connectors</div>
              <div style={{ fontSize: 12.5, color: A.ink2, marginTop: 2 }}>The seven systems that never talked. Health-checked every 5 minutes — a dead connector alerts IT, not the operator.</div></div>
            <Btn primary color="#2B6E7E" label="＋ Add connector" />
          </div>
          <Panel style={{ overflow: "hidden" }}>
            <div style={{ display: "flex", padding: "10px 18px", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.05em", color: A.ink3, borderBottom: `1px solid ${A.border}` }}>
              <span style={{ flex: 2.4 }}>SYSTEM</span><span style={{ flex: 2.6 }}>WHAT IT FEEDS</span><span style={{ flex: 1.2 }}>DIRECTION</span><span style={{ flex: 1 }}>STATUS</span><span style={{ flex: 1 }}>LAST SYNC</span>
            </div>
            {conns.map((c: any, i) => {
              const write = String(c[6]).includes("write");
              return (
              <div key={i} style={{ display: "flex", alignItems: "center", padding: "13px 18px", fontSize: 13, borderBottom: i < conns.length - 1 ? `1px solid ${A.border}` : "none" }}>
                <span style={{ flex: 2.4, display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: A.ink }}><span style={{ fontSize: 16 }}>{c[0]}</span>{c[1]}</span>
                <span style={{ flex: 2.6, color: A.ink2 }}>{c[2]}</span>
                <span style={{ flex: 1.2 }}><Pill t={c[6]} c={write ? A.petrol : A.ink3} bg={write ? "#E1EDEF" : A.soft} /></span>
                <span style={{ flex: 1 }}><Pill t={c[4]} c={c[3]} bg={c[3] === A.green ? "#E3F1E4" : "#F4ECD7"} /></span>
                <span style={{ flex: 1, color: A.ink3 }}>{c[5]}</span>
              </div>
              );
            })}
          </Panel>
          <div style={{ fontSize: 12, color: A.ink3 }}>HubSpot ↔ Monday used to sync in neither direction. The system reconciles them on read — the fix that de-risks everything else.</div>
        </div>
      </AdminShell>
    );
  }

  // ── SYSTEM ADMIN · Model & Box (isolation) ─────────────────────────────────
  if (screen === "isolation") {
    return (
      <AdminShell active="isolation" role="SYSTEM ADMIN" name="GoSystems">
        <div style={{ display: "flex", flexDirection: "column", gap: 16, height: "100%" }}>
          <div><div style={{ fontSize: 20, fontWeight: 700, color: A.ink }}>Model & Box — the isolation boundary</div>
            <div style={{ fontSize: 12.5, color: A.ink2, marginTop: 2 }}>Two tiers by data sensitivity. The engine is swappable per data-class — commercial data can use Claude; mission & intel data never leaves the box.</div></div>
          <Panel style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 12, borderLeft: `3px solid ${A.violet}` }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: A.violet, letterSpacing: "0.05em", whiteSpace: "nowrap" }}>DATA CLASSIFICATION → ROUTING</span>
            <span style={{ fontSize: 12, color: A.ink2, lineHeight: 1.4 }}>Every record is tagged at ingest — <b style={{ color: A.blue }}>commercial</b> (HubSpot/Monday/Outlook) or <b style={{ color: A.petrol }}>mission/intel</b> (Scout/plans/pricing). The tag decides the engine: commercial → Claude; mission → the on-prem open model. A mission tag can never route outward — that's how the system <i>knows</i>.</span>
          </Panel>
          <div style={{ display: "flex", gap: 14, flex: 1, minHeight: 0 }}>
            <Panel style={{ flex: 1, padding: 18, borderTop: `4px solid ${A.blue}` }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: A.blue }}>COMMERCIAL TIER · can reach the web</div>
              <div style={{ marginTop: 8, fontSize: 15, fontWeight: 800, color: A.ink }}>Claude on their own AWS Bedrock</div>
              <div style={{ fontSize: 12, color: A.ink2, marginTop: 6, lineHeight: 1.5 }}>HubSpot · Monday · Outlook · enrichment (Apollo/LinkedIn). In MAX's own account, no training on their data. The natural first proof.</div>
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                {[["Engine", "Claude (Sonnet / Haiku)"], ["Data", "commercial only"], ["Egress", "web allowed (enrichment)"]].map((r: any, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "6px 0", borderBottom: i < 2 ? `1px solid ${A.border}` : "none" }}><span style={{ color: A.ink3 }}>{r[0]}</span><span style={{ fontWeight: 700, color: A.ink }}>{r[1]}</span></div>
                ))}
              </div>
            </Panel>
            <Panel style={{ flex: 1, padding: 18, borderTop: `4px solid ${A.petrol}` }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: A.petrol }}>MISSION / INTEL TIER · air-gapped</div>
              <div style={{ marginTop: 8, fontSize: 15, fontWeight: 800, color: A.ink }}>Self-hosted open model, on-prem</div>
              <div style={{ fontSize: 12, color: A.ink2, marginTop: 6, lineHeight: 1.5 }}>Scout · mission plans · pricing. Runs on MAX's own GPUs — no egress path exists. External AI on ops data is “a huge violation”; this tier answers it.</div>
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                {[["Engine", "Nemotron Super 49B (swappable)"], ["Box", "2× H100 · on-prem"], ["Egress", "none — physics-level isolation"]].map((r: any, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "6px 0", borderBottom: i < 2 ? `1px solid ${A.border}` : "none" }}><span style={{ color: A.ink3 }}>{r[0]}</span><span style={{ fontWeight: 700, color: A.ink }}>{r[1]}</span></div>
                ))}
              </div>
            </Panel>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 14, minHeight: 0 }}>
              <Panel style={{ padding: 16 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 10 }}>Guardrails (operator-locked)</div>
                {[["Approve-before-write on all changes", true], ["Numbers computed in code, never a model", true], ["Mission data cannot touch external models", true], ["Every action written to the audit log", true]].map((g: any, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: i < 3 ? `1px solid ${A.border}` : "none" }}><span style={{ fontSize: 12.5, color: A.ink }}>{g[0]}</span><Toggle on={g[1]} /></div>
                ))}
              </Panel>
              <Panel style={{ padding: 16, flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 10 }}>Audit — every agent action</div>
                {[["09:04", "Reconcile write-back approved", A.green], ["09:01", "Mission MX-2214 flag raised", A.amber], ["08:57", "Lead enriched (commercial tier)", A.blue]].map((a: any, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, fontSize: 12, padding: "7px 0", borderBottom: i < 2 ? `1px solid ${A.border}` : "none" }}>
                    <span style={{ color: A.ink3, fontFamily: "monospace" }}>{a[0]}</span><span style={{ width: 7, height: 7, borderRadius: 999, background: a[2], marginTop: 5 }} /><span style={{ color: A.ink2 }}>{a[1]}</span>
                  </div>
                ))}
              </Panel>
            </div>
          </div>
        </div>
      </AdminShell>
    );
  }

  // ── BUSINESS ADMIN · Org view + approvals + memory ─────────────────────────
  if (screen === "org") {
    return (
      <AdminShell active="org" role="BUSINESS ADMIN" name="Ori / Tzahi" nav={BIZ_NAV} accent="#A2AF6E">
        <div style={{ display: "flex", flexDirection: "column", gap: 16, height: "100%" }}>
          <div><div style={{ fontSize: 20, fontWeight: 700, color: A.ink }}>Org view — the leadership picture</div>
            <div style={{ fontSize: 12.5, color: A.ink2, marginTop: 2 }}>Pipeline reality, coverage, who-approves-what, and org memory — the things leadership could never see because activity only appeared once hand-logged.</div></div>
          <div style={{ display: "flex", gap: 12 }}>
            <Tile label="Pipeline (real)" value="$4.2M" tone={A.green} note="live, not hand-logged" />
            <Tile label="Unworked contacts" value="22,400" tone={A.amber} note="half the DB" />
            <Tile label="Missed inquiries · 7d" value="6" tone={A.red} note="no response > 48h" />
            <Tile label="Overdue invoices" value="20" tone={A.amber} note="$610k" />
          </div>
          <div style={{ display: "flex", gap: 14, flex: 1, minHeight: 0 }}>
            <Panel style={{ flex: 1, padding: 16 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink, marginBottom: 12 }}>Approval definitions — who approves what</div>
              {[["Pricing ≤ $15k", "Head of TS", A.green], ["Pricing $15–25k", "VP Operations", A.amber], ["Pricing > $25k", "COO", A.red], ["Client write-backs", "Record owner", A.petrol]].map((r: any, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: i < 3 ? `1px solid ${A.border}` : "none" }}>
                  <span style={{ fontSize: 12.5, color: A.ink }}>{r[0]}</span><Pill t={r[1]} c={r[2]} bg={A.soft} />
                </div>
              ))}
              <div style={{ marginTop: 12, background: "#EFF5EF", borderRadius: 10, padding: "11px 13px", fontSize: 12, color: A.green, lineHeight: 1.45 }}>The agent drafts, validates and flags — a human always approves. “It backs you up, it doesn't replace you.”</div>
            </Panel>
            <Panel style={{ flex: 1.3, padding: 16, borderTop: `3px solid ${A.petrol}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 9.5, fontWeight: 800, color: "#FFF", background: A.petrol, borderRadius: 999, padding: "3px 8px", letterSpacing: "0.04em" }}>★ TOP VALIDATED USE-CASE</span>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink }}>Org memory — the client club</div>
              </div>
              <div style={{ height: 38, background: A.soft, border: `1px solid ${A.border}`, borderRadius: 9, display: "flex", alignItems: "center", padding: "0 12px", fontSize: 13, color: A.ink2 }}>🔍 “100 companies I served in emergencies, last 3 years”</div>
              <div style={{ marginTop: 12, fontSize: 11.5, fontWeight: 700, color: A.ink3 }}>FROM SAP MISSION CODES · 3.2s</div>
              {[["Aflame Security", "4 missions · EMEA · $1.24M"], ["Northwind Energy", "2 missions · DACH · $180k"], ["Meridian Air", "6 missions · global · $920k"]].map((r: any, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: i < 2 ? `1px solid ${A.border}` : "none" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: A.ink }}>{r[0]}</span><span style={{ fontSize: 11.5, color: A.ink3 }}>{r[1]}</span>
                </div>
              ))}
              <div style={{ marginTop: 10, background: "#E1EDEF", borderRadius: 10, padding: "11px 13px", fontSize: 12, color: A.petrol }}>Proven leads — your “client club.” When a new escalation hits, this is the first outreach list. Anat no longer digs invoices for half a day.</div>
            </Panel>
          </div>
        </div>
      </AdminShell>
    );
  }

  return <div style={{ padding: 40, fontFamily: FONT, color: A.ink3 }}>screen: {String(screen)}</div>;
}

// ── the browser-window frame ─────────────────────────────────────────────────
function ScreenNode({ data }: { data: any }) {
  return (
    <div style={{ width: SCREEN_W, background: A.panel, borderRadius: 16, boxShadow: "0 26px 64px rgba(0,0,0,0.18)", overflow: "hidden", border: `1px solid ${A.border}`, fontFamily: FONT }}>
      <div style={{ height: 44, background: "#ECEEF1", borderBottom: `1px solid ${A.border}`, display: "flex", alignItems: "center", gap: 8, padding: "0 16px" }}>
        {["#ED6A5E", "#F4BF4F", "#61C554"].map((c) => <span key={c} style={{ width: 11, height: 11, borderRadius: 999, background: c }} />)}
        <div style={{ marginLeft: 12, flex: 1, maxWidth: 480, height: 26, background: "#FFF", borderRadius: 8, border: `1px solid ${A.border}`, display: "flex", alignItems: "center", padding: "0 12px", fontSize: 12, color: A.ink3 }}>🔒 max.tailormind.io{data.path}</div>
      </div>
      <ScreenBody screen={data.screen} />
    </div>
  );
}

function ScreenExplainNode({ data }: { data: any }) {
  return (
    <div style={{ width: 600, fontFamily: FONT }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ width: 44, height: 44, borderRadius: 12, background: data.accent, color: "#FFF", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800, flexShrink: 0 }}>{data.num}</span>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", color: data.accent }}>{data.tag}</div>
          <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.02em", color: A.ink, lineHeight: 1.1 }}>{data.title}</div>
        </div>
      </div>
      <div style={{ marginTop: 16, fontSize: 17, lineHeight: 1.55, color: A.ink2 }}>{rich(data.lede)}</div>
      <ul style={{ margin: "18px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 11 }}>
        {data.points.map((p: string, i: number) => (
          <li key={i} style={{ position: "relative", paddingLeft: 22, fontSize: 15, lineHeight: 1.5, color: A.ink2 }}>
            <span style={{ position: "absolute", left: 0, top: 8, width: 8, height: 8, borderRadius: 999, background: data.accent }} />
            {rich(p)}
          </li>
        ))}
      </ul>
      {data.covers && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: A.ink3, marginBottom: 8 }}>COVERS THE PROBLEMS</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {data.covers.map((c: string, i: number) => (
              <span key={i} style={{ fontSize: 12, fontWeight: 700, color: A.petrol, background: "#E1EDEF", borderRadius: 999, padding: "5px 11px" }}>{c}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TitleNode({ data }: { data: any }) {
  return (
    <div style={{ fontFamily: FONT }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ fontSize: 118, fontWeight: 800, lineHeight: 1, letterSpacing: "-0.03em", color: T.ink }}>{data.title}</div>
        <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: "0.04em", color: "#FFFFFF", background: A.petrol, borderRadius: 999, padding: "9px 22px", whiteSpace: "nowrap" }}>{data.tag}</span>
      </div>
      <div style={{ marginTop: 18, fontSize: 26, fontWeight: 500, color: T.ink2, letterSpacing: "-0.01em", maxWidth: 1400, lineHeight: 1.4 }}>{data.sub}</div>
    </div>
  );
}

function ArchPanelNode({ data }: { data: any }) {
  return (
    <div style={{ fontFamily: FONT, width: 1900, background: T.card, border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 14px 38px rgba(28,25,23,0.08)", padding: 30, boxSizing: "border-box" }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", color: A.petrol }}>{data.label}</div>
      <div style={{ marginTop: 8, fontSize: 30, fontWeight: 700, letterSpacing: "-0.02em", color: T.ink }}>{data.title}</div>
      {data.sub && <div style={{ marginTop: 10, fontSize: 16, lineHeight: 1.55, color: T.ink2, maxWidth: 1500 }}>{rich(data.sub)}</div>}
      <div style={{ marginTop: 18 }}><Diagram boxes={data.boxes} arrows={data.arrows} vw={data.vw} vh={data.vh} /></div>
      {data.notes && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {data.notes.map((n: string, i: number) => (
            <div key={i} style={{ fontSize: 13, lineHeight: 1.5, color: T.ink2, background: "#FAF7F2", border: `1px solid ${T.line}`, borderRadius: 10, padding: "10px 12px" }}>{rich(n)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function CoverageNode({ data }: { data: any }) {
  return (
    <div style={{ fontFamily: FONT, width: 1900, background: T.card, border: `1px solid ${T.line}`, borderTop: `4px solid ${A.green}`, borderRadius: 16, boxShadow: "0 14px 38px rgba(28,25,23,0.08)", padding: 30, boxSizing: "border-box" }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", color: A.green }}>{data.label}</div>
      <div style={{ marginTop: 8, fontSize: 30, fontWeight: 700, letterSpacing: "-0.02em", color: T.ink }}>{data.title}</div>
      <div style={{ marginTop: 8, fontSize: 15, color: T.ink2, maxWidth: 1400 }}>{rich(data.lede)}</div>
      <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 18 }}>
        {data.groups.map((g: any, gi: number) => (
          <div key={gi}>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.04em", color: g.color, borderBottom: `2px solid ${g.color}`, paddingBottom: 6, marginBottom: 8 }}>{g.title} · {g.items.length}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {g.items.map((it: any, i: number) => (
                <div key={i} style={{ display: "flex", gap: 6, fontSize: 11.5, lineHeight: 1.35 }}>
                  <span style={{ color: A.green, fontWeight: 800, flexShrink: 0 }}>✓</span>
                  <span style={{ color: T.ink2 }}><b style={{ color: T.ink }}>{it[0]}</b> {it[1]}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 18, fontSize: 13, color: A.green, fontWeight: 700 }}>{data.foot}</div>
    </div>
  );
}

function OutcomesNode({ data }: { data: any }) {
  return (
    <div style={{ fontFamily: FONT, width: 1900, background: A.navy, borderRadius: 16, boxShadow: "0 14px 38px rgba(28,25,23,0.14)", padding: "24px 30px", boxSizing: "border-box" }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", color: A.lime }}>THE OUTCOME · BEFORE → AFTER</div>
      <div style={{ marginTop: 6, fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em", color: "#FFF" }}>What changes for MAX — in one glance</div>
      <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
        {data.items.map((it: any, i: number) => (
          <div key={i} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ fontSize: 12.5, color: "#9BB0C9", textDecoration: "line-through" }}>{it[0]}</div>
            <div style={{ marginTop: 8, fontSize: 17, fontWeight: 800, color: A.lime, letterSpacing: "-0.01em" }}>→ {it[1]}</div>
            <div style={{ marginTop: 6, fontSize: 11.5, color: "#C7D3E2" }}>{it[2]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Product walkthrough: embedded deck (click-through + play) + video modal ──
const WALK_DECK = "/pitch-doc/max-security/system-walkthrough";
const WALK_SLUG = "pitch-doc/max-security/system-walkthrough";
const WALK_VIDEO = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/max-security/system-walkthrough/walkthrough.mp4";

function WalkthroughNode() {
  const [videoOpen, setVideoOpen] = useState(false);
  // Render the deck INLINE via BlockRenderer (the same engine /pitch-doc uses)
  // — NOT an iframe of the app route. This shows ONLY the slides, no sidebar /
  // app chrome, and touches nothing in the shared Layout.
  const api = useApi();
  const [deckBlocks, setDeckBlocks] = useState<Block[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api(`/api/kb/${WALK_SLUG}`);
        if (!res.ok) return;
        const body = (await res.json()) as { content?: { blocks?: Block[] } };
        if (!cancelled && Array.isArray(body.content?.blocks)) {
          setDeckBlocks(body.content!.blocks!);
        }
      } catch {
        /* leave the loader up */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);
  const btnPrimary: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 20px", borderRadius: 999, border: "none", background: A.petrol, color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: FONT, textDecoration: "none" };
  const btnGhost: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 18px", borderRadius: 999, border: `1px solid ${T.line}`, background: T.card, color: T.ink, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: FONT, textDecoration: "none" };
  return (
    <div style={{ fontFamily: FONT, width: 1420, background: T.card, border: `1px solid ${T.line}`, borderTop: `4px solid ${A.petrol}`, borderRadius: 16, boxShadow: "0 14px 38px rgba(28,25,23,0.10)", padding: 26, boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", color: A.petrol }}>PRODUCT WALKTHROUGH</div>
          <div style={{ marginTop: 8, fontSize: 30, fontWeight: 800, letterSpacing: "-0.02em", color: T.ink }}>The whole system in 5 minutes</div>
          <div style={{ marginTop: 8, fontSize: 15, color: T.ink2, maxWidth: 860, lineHeight: 1.5 }}>Click through the deck below and press play to hear each screen explained — or watch the full narrated film.</div>
        </div>
        <div className="nopan nodrag" style={{ display: "flex", gap: 10, pointerEvents: "auto" }}>
          <button type="button" onClick={() => setVideoOpen(true)} style={btnPrimary}>▶ View video</button>
          <a href={WALK_DECK} style={btnGhost}>Open deck ↗</a>
        </div>
      </div>
      <div className="nopan nodrag" style={{ marginTop: 18, width: 1368, height: 784, position: "relative", borderRadius: 14, overflow: "hidden", border: `1px solid ${T.line}`, boxShadow: "0 10px 26px rgba(0,0,0,0.10)", pointerEvents: "auto", background: T.bg }}>
        {deckBlocks ? (
          <BlockRenderer config={pitchDeckConfig} blocks={deckBlocks} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: T.ink2, fontSize: 14, fontFamily: FONT }}>
            Loading deck…
          </div>
        )}
      </div>
      {videoOpen && createPortal(
        <div
          onClick={() => setVideoOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 2147483647, background: "rgba(0,0,0,0.82)", display: "flex", alignItems: "center", justifyContent: "center", padding: 30 }}
        >
          <button
            type="button"
            onClick={() => setVideoOpen(false)}
            aria-label="Close"
            style={{ position: "fixed", top: 20, right: 24, zIndex: 100000, width: 42, height: 42, borderRadius: 999, border: "none", background: "rgba(255,255,255,0.16)", color: "#fff", fontSize: 20, cursor: "pointer" }}
          >
            ✕
          </button>
          <video
            onClick={(e) => e.stopPropagation()}
            src={WALK_VIDEO}
            controls
            autoPlay
            style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 12, boxShadow: "0 30px 80px rgba(0,0,0,0.5)", background: "#000" }}
          />
        </div>,
        document.body,
      )}
    </div>
  );
}

// The strategy / architecture board rendered as one node (sits below the wireframes).
function StrategyNode() {
  return <StrategyBoardNode data={{ width: 3400, groups: STRATEGY_GROUPS }} />;
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION — the board-standard container (Erez-locked 2026-07-19). Every logical
// group on this board is ONE <SectionBox>: a neutral-gray, self-hugging box with
// its short label as the big black pill INSIDE at the top, and its real content
// (the existing node components, unchanged) as white cards within. NOTHING lives
// outside a SectionBox. `rows` is a list of rows; each row is a list of content
// items laid side by side, rows stack down. Many rows → an internal grid, so a
// section stays wider than it is tall.
// ═══════════════════════════════════════════════════════════════════════════

type SecItem = { type: string; data?: any };

// One option: the diagram, and its instruction card directly beneath it. The
// card is short on purpose — a fixed 470px frame is taller than the steps, so
// nothing scrolls and the whole thing reads at a glance in a meeting.
// The one decision: A (Bedrock) or B (Claude Team). Same actor, same systems
// column on both — only the middle column differs, which IS the decision.
const OPTIONS = [
  { Diagram: MaxConnectBedrock, slug: "steps-bedrock", label: "A · Bedrock — their cloud" },
  { Diagram: MaxConnectTeam, slug: "steps-team", label: "B · Claude Team — Anthropic" },
] as const;

function OptionColumn({ n }: { n: 0 | 1 }) {
  const { Diagram, slug, label } = OPTIONS[n];
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 26 }}>
      <Diagram />
      <DocumentFrame slug={`kb-doc/max-security/kickoff/${slug}`} label={label} width={760} height={430} />
    </div>
  );
}

function renderSecItem(it: SecItem, key: number) {
  switch (it.type) {
    case "title": return <TitleNode key={key} data={it.data} />;
    case "walkthrough": return <WalkthroughNode key={key} />;
    case "outcomes": return <OutcomesNode key={key} data={it.data} />;
    case "archPanel": return <ArchPanelNode key={key} data={it.data} />;
    case "coverage": return <CoverageNode key={key} data={it.data} />;
    case "screen": return <ScreenNode key={key} data={it.data} />;
    case "screenExplain": return <ScreenExplainNode key={key} data={it.data} />;
    case "strategy": return <StrategyNode key={key} />;
    // A KB page presented on the board (board law, 2026-07-31: a board is a
    // presented document, a presentation, or a diagram — never prose typed here).
    case "doc": return <DocumentFrame key={key} slug={it.data.slug} label={it.data.label} width={880} height={1240} />;
    // Each option = its diagram with the bare instruction card directly UNDER it.
    // (Erez 2026-08-04: the long documents are hard to consume in a meeting — he
    // wants the steps and nothing else, right next to the picture.) The steps are
    // a KB page shown via DocumentFrame, never prose typed into this file.
    case "option": return <OptionColumn key={key} n={it.data.n} />;
    default: return null;
  }
}

function renderSection(s: { id: string; title: string; rows: SecItem[][] }) {
  // No tall sections: when a section has many rows, lay them in a GRID (a few
  // columns) so the section is wider than it is tall.
  const n = s.rows.length;
  const cols = n > 6 ? 3 : n > 3 ? 2 : 1;
  return (
    <SectionBox key={s.id} title={s.title} layout="custom">
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, max-content)`, columnGap: 100, rowGap: 72, alignItems: "start" }}>
        {s.rows.map((row, ri) => (
          <div key={ri} style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 64 }}>
            {row.map((it, ci) => renderSecItem(it, ci))}
          </div>
        ))}
      </div>
    </SectionBox>
  );
}

// The whole board is ONE node: a SectionGrid that packs all the section boxes
// into a compact grid (columns fill top-down, then across).
function GridNode({ data }: { data: { defs: { id: string; title: string; rows: SecItem[][] }[] } }) {
  return (
    <SectionGrid>
      {data.defs.map(renderSection)}
    </SectionGrid>
  );
}

const nodeTypes: NodeTypes = {
  grid: GridNode as unknown as NodeTypes[string],
};

// ── system overview diagram ──────────────────────────────────────────────────
const OVERVIEW: any = {
  label: "THE SYSTEM · ONE PAGE",
  title: "The Single Brain + per-role agents, on MAX's own box",
  sub: "Seven systems that never talked feed one **connected, persistent, trustworthy record** per entity. Per-role agents read it, **draft · validate · flag** — and every change passes a human **approve-gate** before it writes back. Commercial data can use Claude; **mission & intel data runs on-prem, air-gapped.**",
  vw: 1300, vh: 400,
  boxes: [
    { x: 20, y: 40, w: 190, h: 120, label: "7 systems", sub: "HubSpot · Monday · Firefly · Outlook · Scout · SAP · WhatsApp", tone: "person" },
    { x: 290, y: 70, w: 180, h: 60, label: "Connectors", sub: "read + reconcile", tone: "work" },
    { x: 540, y: 55, w: 210, h: 90, label: "Single Brain", sub: "one record / entity · pgvector", tone: "data" },
    { x: 820, y: 20, w: 200, h: 56, label: "Sales / Ops / Mktg / Cons agents", sub: "draft · validate · flag", tone: "agent" },
    { x: 820, y: 120, w: 200, h: 56, label: "Guardrail + approve gate", sub: "human approves", tone: "report" },
    { x: 1090, y: 70, w: 180, h: 60, label: "Write-back", sub: "into their systems", tone: "person" },
    { x: 540, y: 250, w: 210, h: 90, label: "Commercial tier", sub: "Claude · Bedrock · web ok", tone: "work" },
    { x: 820, y: 250, w: 200, h: 90, label: "Mission / intel tier", sub: "open model · on-prem · air-gapped", tone: "data" },
  ],
  arrows: [
    { x1: 210, y1: 100, x2: 290, y2: 100, label: "pull" },
    { x1: 470, y1: 100, x2: 540, y2: 100, label: "reconcile" },
    { x1: 750, y1: 90, x2: 820, y2: 48 },
    { x1: 750, y1: 110, x2: 820, y2: 148, label: "propose" },
    { x1: 1020, y1: 148, x2: 1090, y2: 100, label: "approved ✓" },
    { x1: 645, y1: 145, x2: 645, y2: 250, dashed: true, label: "engine by data-class" },
    { x1: 750, y1: 295, x2: 820, y2: 295, dashed: true, color: "#B85638" },
  ],
  notes: [
    "**The spine:** every “critical” pain is a symptom of one fault — no connected, persistent record of an entity across its lifecycle. Build the record first; everything else is a feature on top.",
    "**Approve-before-change is law** — the agent never writes silently. It shows the story + the contradictions; the human approves, edits, or rejects.",
  ],
};

const COVERAGE: any = {
  label: "COVERAGE · NOTHING IGNORED",
  title: "All 68 discovery problems → the screen that answers each",
  lede: "Every pain from every discovery session, each mapped to a screen above — the checklist the design is validated against. SALES-13..18 and OPS-19..22 fold in the Jul-14 validation-session pains (the department analyses ranked 12 Sales / 18 Ops; the Jul-14 session added the rest). OPS-13 (OneDrive tree) and OPS-18 (standing up the function) are out of product scope — not claimed.",
  foot: "68 / 68 in-scope problems mapped · 3 constraints designed-in (isolation · approve-before-change · contradiction-flagging).",
  groups: [
    { title: "SALES", color: "#2F6BB0", items: [["S1", "Snapshot·Reconcile"], ["S2", "Briefing"], ["S3", "Briefing"], ["S4", "Snapshot"], ["S5", "Enrich"], ["S6", "Connectors"], ["S7", "Briefing"], ["S8", "Qi·Snapshot"], ["S9", "Legal"], ["S10", "Briefing"], ["S11", "Snapshot"], ["S12", "Org"], ["S13", "Snapshot"], ["S14", "Enrich"], ["S15", "Enrich"], ["S16", "Briefing"], ["S17", "Briefing"], ["S18", "Qi·Org"]] },
    { title: "MARKETING", color: "#6B4FA0", items: [["M1", "Enrich·MQL"], ["M2", "Connectors·Reconcile"], ["M3", "MQL"], ["M4", "MQL"], ["M5", "MQL"], ["M6", "MQL"], ["M7", "MQL"], ["M8", "MQL"], ["M9", "MQL"], ["M10", "Snapshot·Conn"], ["M11", "Enrich"], ["M12", "Enrich"]] },
    { title: "OPERATIONS", color: "#2B5560", items: [["O1", "Snapshot·Mission"], ["O2", "Pricing"], ["O3", "Org·Snapshot"], ["O4", "Mission"], ["O5", "Mission"], ["O6", "Mission"], ["O7", "Qi"], ["O8", "Qi"], ["O9", "Mission"], ["O10", "Connectors"], ["O11", "Qi"], ["O12", "Snapshot"], ["O14", "Org"], ["O15", "Pricing"], ["O16", "Mission"], ["O17", "Qi"], ["O19", "Mission"], ["O20", "Pricing"], ["O21", "Pricing"], ["O22", "Org"]] },
    { title: "CONSULTING", color: "#1F8A80", items: [["C1", "Survey"], ["C2", "Survey"], ["C3", "Survey"], ["C4", "Survey"], ["C5", "Survey"], ["C6", "Survey"], ["C7", "Survey"]] },
    { title: "CROSS-CUTTING", color: "#B85638", items: [["X1", "Snapshot"], ["X2", "Reconcile"], ["X3", "all agents"], ["X4", "Snapshot·Mission"], ["X5", "Enrich"], ["X6", "MQL"], ["X7", "Org"], ["X8", "Org·Isolation"], ["X9", "Briefing"], ["X10", "Org·Briefing"], ["X11", "Org"]] },
  ],
};

// ── the screens, per band (screen id · path · explain card) ──────────────────
const CORE_SCREENS: any[] = [
  { screen: "snapshot", path: "/clients/aflame", num: 1, tag: "THE SPINE", accent: "#2B5560",
    title: "Client Snapshot 360",
    lede: "One entity, assembled from **seven systems** into a single record. The name that was “Aflam / Aflame / Aflame Security Ltd” is reconciled; every fact carries where it came from.",
    points: ["Identity, personas, lifecycle, mission history, commercials, Scout intel and the WhatsApp gold — in one view", "Source-provenance on every fact; the record is the truth, not any one system", "This is the “single brain” per client — meeting-prep, pricing and outreach are all features on top"],
    covers: ["SALES-1", "SALES-4", "SALES-11", "SALES-13", "OPS-1", "OPS-3", "OPS-12", "XCUT-1", "XCUT-4", "XCUT-7"] },
  { screen: "reconcile", path: "/reconcile/aflame", num: 2, tag: "THE SIGNATURE", accent: "#B85638",
    title: "Reconciliation — sources disagree",
    lede: "The soul of the product. When HubSpot, Monday and SAP disagree, the agent shows the **story + the contradictions** and **writes nothing until a human approves.**",
    points: ["Side-by-side sources with every mismatch in red; big variance → “⚠ needs validation”", "A plain-language account of what it matched and why — read it, don't decode it", "**Approve · Edit · Reject** — the approve-before-change law, made visible"],
    covers: ["XCUT-2", "SALES-1", "OPS-7", "CONSTR-2", "CONSTR-3"] },
  { screen: "enrich", path: "/enrich/new", num: 3, tag: "THE SEAM", accent: "#2E7D32",
    title: "Enrichment at capture",
    lede: "The marketing→sales seam, fixed. A corporate email arrives; **waterfall enrichment** fills person + city, scores an MQL, and tiers by product.",
    points: ["A real **waterfall**: Clearbit misses → falls through to Apollo → LinkedIn (first hit wins), verified email + title + city", "An **explainable** MQL: seniority / intent / fit sub-scores, then a **per-product** tier (Academy $2k ≠ Platform $80k)", "No more “one qualified flag” dumped on sales — the tier drives the routing"],
    covers: ["MKT-1", "MKT-11", "MKT-12", "SALES-5", "SALES-14", "SALES-15", "XCUT-5"] },
];
const SALES_SCREENS: any[] = [
  { screen: "briefing", path: "/briefing", num: 4, tag: "SALES · AE", accent: "#2F6BB0",
    title: "Daily Briefing",
    lede: "45,000 contacts, half unworked — the capacity wall. The agent reads them all overnight and hands the AE **ten that matter, each with a drafted first touch.**",
    points: ["The DB is the DB: SQL narrows 45,000 → 10; the agent only synthesizes the shortlist", "Contact-moves radar (Siemens → BMW), new-decision-maker signals, renewal windows", "**Coach, not send** — drafts in the AE's voice; the human always sends"],
    covers: ["SALES-2", "SALES-3", "SALES-7", "SALES-10", "SALES-16", "SALES-17", "XCUT-9"] },
  { screen: "legal", path: "/legal/review", num: 5, tag: "SALES · LEGAL OPS", accent: "#B07A1E",
    title: "Legal & Compliance review",
    lede: "~30 reviews a month, ~90% repeating questions. The agent answers the repeats **from the clause corpus, citing each clause** — and flags only what's genuinely new.",
    points: ["Detects the questions in a contract/addendum, splits repeat vs new", "Drafts **cited** answers — never invents legal language; novel questions go to a human", "~50% of legal/compliance time recovered"],
    covers: ["SALES-9"] },
];
const OPS_SCREENS: any[] = [
  { screen: "mission", path: "/missions/mx-2214", num: 6, tag: "OPERATIONS · RM", accent: "#2B5560",
    title: "Mission Plan Builder",
    lede: "The RM was the integration layer — 4 managers, 2 operators, the portal, 20–25 docs. Now the plan **assembles itself from the record; the RM judges.**",
    points: ["Driver, vehicle, timing, GSOC auto-pulled from parameters; 22 docs drafted", "A **verification gate** flags skipped steps and the Scout wider-picture miss (a G7 summit across the border) before anything ships", "Runs on the on-prem, air-gapped tier — external AI on ops data is a non-starter"],
    covers: ["OPS-4", "OPS-5", "OPS-6", "OPS-9", "OPS-16", "OPS-19", "XCUT-3"] },
  { screen: "pricing", path: "/pricing/matrix", num: 7, tag: "OPERATIONS · RM", accent: "#6B4FA0",
    title: "Trouble Matrix — pricing engine",
    lede: "Eyal's own risk model (8 topics, 17 parameters, ~80% built) wired to Scout. Risk → composition → **price, computed in code** — non-numbers people stop pricing by hand.",
    points: ["Risk levels synced from the intel portal, not hand-set guesses", "A transparent compute card: base × risk × emergency × VAT × FX — the 250%-vs-75% error can't happen", "**Dynamic**: a border closes → vendor cost up → price re-computed within the hour"],
    covers: ["OPS-2", "OPS-15", "OPS-20", "OPS-21"] },
  { screen: "qi", path: "/reconcile/quote-invoice", num: 8, tag: "OPERATIONS · RM", accent: "#B07A1E",
    title: "Quote ↔ Invoice reconciliation",
    lede: "Quote and invoice diverge 25–30%, rebuilt by hand every time. The agent **copy-forwards, pulls the hours-on-ground, and flags every delta** for the manager.",
    points: ["Line-by-line variance with the source of each delta (overtime from Monday, added assets from the change log)", "An **itinerary diff** — the six-day plan resent with one line changed, highlighted", "Manager reads the flags, not 20 raw lines — then approves"],
    covers: ["OPS-7", "OPS-8", "OPS-11", "OPS-17", "SALES-8", "SALES-18"] },
];
const MKTCONS_SCREENS: any[] = [
  { screen: "mql", path: "/marketing/mql", num: 9, tag: "MARKETING", accent: "#6B4FA0",
    title: "MQL & Attribution",
    lede: "A real MQL layer, segmentation, nurture, and a **campaign→MQL→revenue** chain that finally closes — the seam that lost 30 minutes a day, fixed.",
    points: ["Leads scored before they reach sales; the un-segmented 6-sends-a-day blast is killed", "Nurture sequences rescue the 30-day backlog automatically", "GA ↔ HubSpot joined → “which marketing made revenue” has an answer"],
    covers: ["MKT-1", "MKT-3", "MKT-4", "MKT-5", "MKT-6", "MKT-7", "MKT-8", "MKT-9", "XCUT-6"] },
  { screen: "survey", path: "/consulting/survey", num: 10, tag: "CONSULTING", accent: "#1F8A80",
    title: "Survey → Report engine",
    lede: "The report that took several days, drafted in minutes. The **benchmark DB scores the site**; the consultant supplies the site-specific judgment.",
    points: ["Intake → risk-matrix → gaps → recommendations, all pre-populated", "A **“how the adversary sees you”** visual report — ~10 pages, high-impact, not a boring page with a photo", "The two-person write-up ceiling lifts; judgment stays human"],
    covers: ["CONS-1", "CONS-2", "CONS-3", "CONS-4", "CONS-5", "CONS-6", "CONS-7"] },
];
const SYS_SCREENS: any[] = [
  { screen: "connectors", path: "/admin/connectors", num: 11, tag: "SYSTEM ADMIN", accent: "#2B6E7E",
    title: "Connectors",
    lede: "The seven systems that never talked, wired and health-checked. HubSpot ↔ Monday — which synced in neither direction — **reconciled on read.**",
    points: ["Status, last-sync, auth and **read-only vs write-back direction** per system", "The Firefly-writes-to-Notes config issue, surfaced and re-routed to the meeting log", "WhatsApp captured — the gold that was connected to nothing"],
    covers: ["XCUT-2", "SALES-6", "OPS-10", "MKT-2", "MKT-10"] },
  { screen: "isolation", path: "/admin/model-box", num: 12, tag: "SYSTEM ADMIN", accent: "#2B5560",
    title: "Model & Box — isolation",
    lede: "Two tiers by data sensitivity. Commercial data can use **Claude on their Bedrock**; mission & intel run a **self-hosted open model on-prem, air-gapped** — the engine swaps per data-class.",
    points: ["The boundary made explicit: what may reach the web vs what never leaves the box", "Operator-locked guardrails: approve-before-write, numbers-in-code, mission-data-never-external", "Every agent action in an immutable audit log"],
    covers: ["CONSTR-1", "CONSTR-3", "XCUT-8"] },
];
const BIZ_SCREENS: any[] = [
  { screen: "org", path: "/admin/org", num: 13, tag: "BUSINESS ADMIN", accent: "#A2AF6E",
    title: "Org view + approvals + memory",
    lede: "The leadership picture that never existed because activity only appeared once hand-logged: real pipeline, coverage gaps, **who-approves-what**, and queryable **org memory.**",
    points: ["Pipeline reality, unworked-half-the-DB, missed-inquiry and overdue flags", "The approval-definitions doc — human approval stays; the agent drafts, validates, flags", "Org memory: “100 companies I served in emergencies, last 3 years” — from SAP mission codes, in seconds"],
    covers: ["SALES-12", "OPS-14", "XCUT-7", "XCUT-8", "XCUT-10", "OPS-3", "OPS-22", "XCUT-11"] },
];

// Each screen band → rows of [screen mock, explanation card] laid side by side.
const screenRows = (arr: any[]): SecItem[][] =>
  arr.map((s) => [
    { type: "screen", data: { screen: s.screen, path: s.path } },
    { type: "screenExplain", data: s },
  ]);

function buildNodes(): Node[] {
  // Every section is ONE SectionBox; ALL of them live in ONE SectionGrid node,
  // which packs them into a wide rectangle. Nothing lives outside a SectionBox.
  const defs: { id: string; title: string; rows: SecItem[][] }[] = [
    { id: "sec-overview", title: "The System", rows: [
      [{ type: "title", data: { title: "MAX Security", tag: "THE SYSTEM", sub: "One system — like Boiling Offers, for MAX. The Single Brain + per-role agents on MAX's own isolated box, solving the problems they told us: no 360 picture, un-enriched leads, systems that disagree." } }],
      [{ type: "outcomes", data: { items: [["22,500 contacts unworked", "worked, ranked daily", "the capacity wall comes down"], ["4-hour plan by hand", "assembled in minutes", "the RM stops being the integration layer"], ["quote↔invoice 25–30% off", "every delta flagged", "priced in code, not guessed"], ["attribution unanswerable", "campaign→revenue traced", "the loop finally closes"]] } }],
    ] },
    { id: "sec-walkthrough", title: "Walkthrough", rows: [[{ type: "walkthrough" }]] },
    // How we actually connect to MAX's systems + the security design for the box
    // it all runs on. Two KB pages, presented side by side (wider than tall).
    // THE ONE DECISION (Erez, 2026-08-04): Bedrock or Claude Team. Everything
    // else — read-only users, MCP wiring, the work — is the same either way.
    // Two self-contained diagrams side by side, each with its own step card.
    { id: "sec-connect-options", title: "The One Decision", rows: [
      [{ type: "option", data: { n: 0 } }, { type: "option", data: { n: 1 } }],
    ] },
    // The CEO meeting, 2026-08-06 — where the hours-tracking problem got a
    // number attached to it. English analysis + the raw Hebrew transcript.
    { id: "sec-dror", title: "CEO Meeting — Dror", rows: [[
      { type: "doc", data: { slug: "kb-doc/max-security/meetings/dror-2026-08-06", label: "Summary, translation + my read" } },
      { type: "doc", data: { slug: "kb-doc/max-security/meetings/dror-2026-08-06-transcript-he", label: "תמלול גולמי — עברית" } },
    ]] },
    // Out of the 2026-08-04 CIO meeting: two emails to send. Sela administer
    // MAX's AWS; the leadership note carries the named per-system asks.
    // 4 + 5 come out of the 2026-08-12 Sela meeting (Tal + Aviel): Tzahi gets the
    // summary he missed, Sela get the requirements doc their architects asked for.
    { id: "sec-emails", title: "Emails to Send", rows: [[
      { type: "doc", data: { slug: "kb-doc/max-security/kickoff/email-sela", label: "1 — Sela · the AWS account" } },
      { type: "doc", data: { slug: "kb-doc/max-security/kickoff/email-leadership", label: "2 — Leadership · the summary + asks" } },
      { type: "doc", data: { slug: "kb-doc/max-security/kickoff/email-tzahi-computers", label: "3 — Tzahi · the new computers" } },
    ], [
      { type: "doc", data: { slug: "kb-doc/max-security/kickoff/email-tzahi-sela-meeting", label: "4 — Tzahi · the Sela meeting summary" } },
      { type: "doc", data: { slug: "kb-doc/max-security/kickoff/email-sela-requirements", label: "5 — Sela · the requirements doc" } },
    ]] },
    { id: "sec-connect", title: "Connecting to MAX", rows: [[
      { type: "doc", data: { slug: "kb-doc/max-security/kickoff/connectivity-plan", label: "The plan — what we ask MAX for" } },
      { type: "doc", data: { slug: "kb-doc/max-security/kickoff/aws-box-security", label: "The secure AWS box — security design" } },
      { type: "doc", data: { slug: "kb-doc/max-security/kickoff/option-zero-steps", label: "Option 0 — step by step" } },
      { type: "doc", data: { slug: "kb-doc/max-security/kickoff/meeting-prep-tal", label: "Meeting prep — Tal (Sela, AWS)" } },
      { type: "doc", data: { slug: "kb-doc/max-security/kickoff/aws-bedrock-technical-brief", label: "AWS + Bedrock — technical brief" } },
    ]] },
    { id: "sec-map", title: "System Map", rows: [[{ type: "archPanel", data: OVERVIEW }]] },
    { id: "sec-core", title: "The Core", rows: screenRows(CORE_SCREENS) },
    { id: "sec-sales", title: "Sales", rows: screenRows(SALES_SCREENS) },
    { id: "sec-ops", title: "Operations", rows: screenRows(OPS_SCREENS) },
    { id: "sec-mktcons", title: "Marketing & Consulting", rows: screenRows(MKTCONS_SCREENS) },
    { id: "sec-sys", title: "System Admin", rows: screenRows(SYS_SCREENS) },
    { id: "sec-biz", title: "Business Admin", rows: screenRows(BIZ_SCREENS) },
    { id: "sec-coverage", title: "Coverage", rows: [[{ type: "coverage", data: COVERAGE }]] },
    { id: "sec-strategy", title: "Strategy & Architecture", rows: [[{ type: "strategy" }]] },
  ];

  return [{ id: "board-grid", type: "grid", position: { x: 0, y: 0 }, draggable: false, data: { defs } }];
}

export function MaxSecuritySystemBoard() {
  const nodes = useMemo(() => buildNodes(), []);
  // Solo mode: /max-security-system?solo=<screenKey> renders a single full-size
  // screen on a plain background (used to capture clean images for the deck).
  const solo = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("solo") : null;
  if (solo) {
    return (
      <div style={{ position: "absolute", inset: 0, background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT }}>
        <ScreenNode data={{ screen: solo, path: `/${solo}` }} />
      </div>
    );
  }
  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <BoardCanvas nodes={nodes} nodeTypes={nodeTypes} fitViewPadding={0.12} />
    </div>
  );
}

export default MaxSecuritySystemBoard;
